import { useState, useEffect, Fragment } from "react";
import {
  type Driver,
  type User,
  type Truck,
  type DumpsterSize,
  type Landfill,
  type Customer,
  type Property,
  type Tenant,
  type PurgePreview,
  type PurgeResult,
  type NewTruck,
  type NewDumpsterSize,
  type NewLandfill,
  type NewCustomer,
  type NewProperty,
  type CustomerEditReason,
  type PropertyEditReason,
  EDIT_REASON_LABELS,
  type TimeclockTag,
  type WebauthnCredentialInfo,
  type WebauthnCredentialsListResponse,
  type WebauthnRegisterBeginResponse,
} from "@eagledispatch/shared";
import { apiFetch, ApiError } from "../../lib/api";
import {
  executeWebAuthnRegistration,
  mapWebAuthnError,
  browserSupportsWebAuthn,
} from "../../lib/webauthn";
import { parseDollarsToCents, MAX_TONS } from "../../lib/parse-amount";
import Button from "../../components/Button";
import Card from "../../components/Card";
import FieldError from "../../components/FieldError";
import type { OfficeRoute } from "./OfficeHome";

/**
 * Client-side NFC UID normalization — mirrors `normalizeTagUid` in
 * `packages/shared/src/timeclock.ts` with VERBATIM error messages.
 */
function normalizeUidClient(raw: string): string {
  if (typeof raw !== "string") {
    throw new Error("Tag UID must be a string");
  }
  const cleaned = raw.replace(/[:\s-]/g, "").toUpperCase();
  if (cleaned.length === 0) {
    throw new Error("Tag UID cannot be empty");
  }
  if (!/^[0-9A-F]+$/.test(cleaned)) {
    throw new Error("Tag UID must be valid hexadecimal characters");
  }
  if (![8, 14, 20].includes(cleaned.length)) {
    throw new Error(
      `Invalid Tag UID length (${cleaned.length} hex chars). Expected 8, 14, or 20 hex characters (4, 7, or 10 bytes)`,
    );
  }
  return cleaned;
}

function formatLocalDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type AdminTab =
  | "drivers"
  | "dispatchers"
  | "trucks"
  | "sizes"
  | "landfills"
  | "customers"
  | "settings";

export default function Admin({
  onNavigate: _onNavigate,
  onToast,
}: {
  onNavigate: (_route: OfficeRoute) => void;
  onToast?: (msg: string, kind?: "success" | "error") => void;
}) {
  const [activeTab, setActiveTab] = useState<AdminTab>("drivers");
  const [error, setError] = useState<string | null>(null);

  // Drivers State
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [driverPin, setDriverPin] = useState("");
  const [driverFieldErrors, setDriverFieldErrors] = useState<{ name?: string; pin?: string }>({});
  const [driverSubmitting, setDriverSubmitting] = useState(false);
  const [removingDriver, setRemovingDriver] = useState<Driver | null>(null);
  const [driverRemovalError, setDriverRemovalError] = useState<string | null>(null);
  const [driverRemovalSubmitting, setDriverRemovalSubmitting] = useState(false);

  // Edit/Unlock Driver
  const [editingDriverId, setEditingDriverId] = useState<string | null>(null);
  const [editDriverName, setEditDriverName] = useState("");
  const [editDriverPhone, setEditDriverPhone] = useState("");
  const [editDriverPin, setEditDriverPin] = useState("");

  const loadDrivers = () => {
    setDriversLoading(true);
    apiFetch<Driver[]>("/api/v1/drivers?includeInactive=true")
      .then((res) => {
        setDrivers(res);
        setDriversLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setDriversLoading(false);
      });
  };

  const handleCreateDriver = async (e: React.FormEvent) => {
    e.preventDefault();
    setDriverFieldErrors({});
    const errors: Record<string, string> = {};
    if (!driverName.trim()) errors.name = "Driver name is required";
    if (!/^\d{4}$/.test(driverPin)) errors.pin = "PIN must be exactly 4 digits";

    if (Object.keys(errors).length > 0) {
      setDriverFieldErrors(errors);
      return;
    }

    setDriverSubmitting(true);
    try {
      await apiFetch<Driver>("/api/v1/drivers", {
        method: "POST",
        body: JSON.stringify({
          name: driverName.trim(),
          phone: driverPhone.trim() || undefined,
          pin: driverPin,
        }),
      });
      setShowAddDriver(false);
      setDriverName("");
      setDriverPhone("");
      setDriverPin("");
      loadDrivers();
      if (onToast) onToast("Driver added successfully.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDriverSubmitting(false);
    }
  };

  const handleRemoveDriver = async () => {
    if (!removingDriver) return;
    setDriverRemovalSubmitting(true);
    setDriverRemovalError(null);
    try {
      await apiFetch<Driver>(`/api/v1/drivers/${removingDriver.id}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: false }),
      });
      setRemovingDriver(null);
      loadDrivers();
      if (onToast) onToast("Driver removed.");
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.status === 409) {
        setDriverRemovalError(apiErr.message);
      } else {
        setDriverRemovalError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setDriverRemovalSubmitting(false);
    }
  };

  const handleRestoreDriver = async (driver: Driver) => {
    try {
      await apiFetch<Driver>(`/api/v1/drivers/${driver.id}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: true }),
      });
      loadDrivers();
      if (onToast) onToast("Driver restored.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleUpdateDriver = async (id: string, unblockOnly = false) => {
    try {
      const payload: Record<string, unknown> = {};
      if (unblockOnly) {
        payload.unblockPin = true;
      } else {
        if (editDriverName.trim()) payload.name = editDriverName.trim();
        payload.phone = editDriverPhone.trim() || null;
        if (editDriverPin.trim()) {
          if (!/^\d{4}$/.test(editDriverPin)) {
            if (onToast) onToast("PIN must be 4 digits", "error");
            return;
          }
          payload.pin = editDriverPin;
        }
      }

      await apiFetch<Driver>(`/api/v1/drivers/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setEditingDriverId(null);
      loadDrivers();
      if (onToast) onToast(unblockOnly ? "PIN unlocked." : "Driver updated.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // SPEC-67 §3.6 break-glass #1 — per-driver device reset. Mirrors the
  // Remove-Driver modal flow. `flipped` (0|1) distinguishes a real reset from
  // an idempotent re-click; the two toasts MUST NOT collapse into one.
  const [resettingDriver, setResettingDriver] = useState<Driver | null>(null);
  const [resetDeviceError, setResetDeviceError] = useState<string | null>(null);
  const [resetDeviceSubmitting, setResetDeviceSubmitting] = useState(false);

  const handleResetDevice = async () => {
    if (!resettingDriver) return;
    setResetDeviceSubmitting(true);
    setResetDeviceError(null);
    const driver = resettingDriver;
    // Optimistic chip flip → Not bound; server truth reloads on settle below.
    setDrivers((rows) =>
      rows.map((d) =>
        d.id === driver.id ? { ...d, deviceBound: false, deviceBoundAt: null } : d,
      ),
    );
    try {
      const res = await apiFetch<{
        ok: boolean;
        deviceBound: boolean;
        flipped: number;
      }>(`/api/v1/drivers/${driver.id}/reset-device`, { method: "POST" });
      setResettingDriver(null);
      if (onToast) {
        if (res.flipped === 0) onToast("Already unbound — nothing to reset.");
        else onToast("Device reset.");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setResetDeviceError(msg);
      if (onToast) onToast(msg, "error");
    } finally {
      setResetDeviceSubmitting(false);
      loadDrivers();
    }
  };

  // SPEC-68 §3.6 break-glass #3 — per-driver passkey reset. Mirrors the
  // Reset-Device flow above. `deleted` (0|n) distinguishes a real reset from
  // an idempotent re-click (SPEC-68 §3.6: deleted 0 → ZERO server side
  // effects, so the two toasts MUST NOT collapse into one).
  const [resettingPasskeyDriver, setResettingPasskeyDriver] = useState<Driver | null>(null);
  const [resetPasskeyError, setResetPasskeyError] = useState<string | null>(null);
  const [resetPasskeySubmitting, setResetPasskeySubmitting] = useState(false);

  const handleResetPasskey = async () => {
    if (!resettingPasskeyDriver) return;
    setResetPasskeySubmitting(true);
    setResetPasskeyError(null);
    const driver = resettingPasskeyDriver;
    // Optimistic chip flip → Not set; server truth reloads on settle below.
    setDrivers((rows) =>
      rows.map((d) =>
        d.id === driver.id ? { ...d, hasPasskey: false, passkeySynced: null } : d,
      ),
    );
    try {
      const res = await apiFetch<{
        ok: boolean;
        hasPasskey: boolean;
        deleted: number;
      }>(`/api/v1/drivers/${driver.id}/reset-passkey`, { method: "POST" });
      setResettingPasskeyDriver(null);
      if (onToast) {
        if (res.deleted === 0) onToast("Already reset — no passkey on file.");
        else onToast("Passkey reset.");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setResetPasskeyError(msg);
      if (onToast) onToast(msg, "error");
    } finally {
      setResetPasskeySubmitting(false);
      loadDrivers();
    }
  };

  // -- Dispatchers State -----------------------------------------------------
  const [dispatchers, setDispatchers] = useState<User[]>([]);
  const [dispatchersLoading, setDispatchersLoading] = useState(false);
  // Unit #19 / #20: per-row toggle state.
  const [togglingDispatcherId, setTogglingDispatcherId] = useState<string | null>(null);
  const [showAddDisp, setShowAddDisp] = useState(false);
  const [dispUsername, setDispUsername] = useState("");
  const [dispPassword, setDispPassword] = useState("");
  const [dispName, setDispName] = useState("");
  const [dispPhone, setDispPhone] = useState("");
  const [dispErrors, setDispErrors] = useState<{ username?: string; password?: string; name?: string }>({});
  const [dispSubmitting, setDispSubmitting] = useState(false);
  const [removingDispatcher, setRemovingDispatcher] = useState<User | null>(null);
  const [dispRemovalError, setDispRemovalError] = useState<string | null>(null);
  const [dispRemovalSubmitting, setDispRemovalSubmitting] = useState(false);

  // SPEC-75 — per-dispatcher break-glass: Reset Device (mirrors the driver
  // Reset-Device flow) + Reset Password (unlocks the 3-strike lockout).
  const [resettingDeviceDispatcher, setResettingDeviceDispatcher] = useState<User | null>(null);
  const [resetDeviceDispatcherError, setResetDeviceDispatcherError] = useState<string | null>(null);
  const [resetDeviceDispatcherSubmitting, setResetDeviceDispatcherSubmitting] = useState(false);
  const [resettingPasswordDispatcher, setResettingPasswordDispatcher] = useState<User | null>(null);
  const [resetPasswordDispatcherError, setResetPasswordDispatcherError] = useState<string | null>(null);
  const [resetPasswordDispatcherSubmitting, setResetPasswordDispatcherSubmitting] = useState(false);
  const [dispNewPassword, setDispNewPassword] = useState("");
  const [dispNewPasswordConfirm, setDispNewPasswordConfirm] = useState("");

  // DISPATCHER-EDIT (PM 2026-09-19) — inline per-row edit form
  // (name / username / password). Mirrors the customers-tab
  // `admin-inline-form` idiom: Edit button opens the row's form, Save/Cancel
  // close it. Phone is deliberately NOT editable (out of scope).
  const [editingDispatcherId, setEditingDispatcherId] = useState<string | null>(null);
  const [editDispName, setEditDispName] = useState("");
  const [editDispUsername, setEditDispUsername] = useState("");
  const [editDispPassword, setEditDispPassword] = useState("");
  const [editDispErrors, setEditDispErrors] = useState<{ name?: string; username?: string; password?: string }>({});
  const [editDispError, setEditDispError] = useState<string | null>(null);
  const [editDispSubmitting, setEditDispSubmitting] = useState(false);

  const loadDispatchers = () => {
    setDispatchersLoading(true);
    apiFetch<User[]>("/api/v1/dispatchers?includeInactive=true")
      .then((res) => {
        setDispatchers(res);
        setDispatchersLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setDispatchersLoading(false);
      });
  };

  const handleRemoveDispatcher = async () => {
    if (!removingDispatcher) return;
    setDispRemovalSubmitting(true);
    setDispRemovalError(null);
    try {
      await apiFetch<User>(`/api/v1/dispatchers/${removingDispatcher.id}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: false }),
      });
      setRemovingDispatcher(null);
      loadDispatchers();
      if (onToast) onToast("Dispatcher removed.");
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setDispRemovalError(apiErr.message || (err instanceof Error ? err.message : String(err)));
    } finally {
      setDispRemovalSubmitting(false);
    }
  };

  const handleRestoreDispatcher = async (dispatcher: User) => {
    try {
      await apiFetch<User>(`/api/v1/dispatchers/${dispatcher.id}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: true }),
      });
      loadDispatchers();
      if (onToast) onToast("Dispatcher restored.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // DISPATCHER-EDIT (PM 2026-09-19) — open the inline edit form for one row,
  // seeded from the row (password always starts blank).
  const openEditDispatcher = (u: User) => {
    setShowAddDisp(false);
    setEditingDispatcherId(u.id);
    setEditDispName(u.name ?? "");
    setEditDispUsername(u.username ?? "");
    setEditDispPassword("");
    setEditDispErrors({});
    setEditDispError(null);
  };

  // DISPATCHER-EDIT — Save. Client validation mirrors POST (name/username
  // required non-blank, password optional-but-min-6). Only CHANGED fields are
  // sent; an empty password omits the key entirely (leave-current semantics).
  const handleUpdateDispatcher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDispatcherId || editDispSubmitting) return;
    setEditDispErrors({});
    setEditDispError(null);

    const current = dispatchers.find((u) => u.id === editingDispatcherId);
    const payload: Record<string, string> = {};
    const errors: { name?: string; username?: string; password?: string } = {};

    const nameTrimmed = editDispName.trim();
    if (!nameTrimmed) {
      errors.name = "Full name is required";
    } else if (nameTrimmed.length > 100) {
      errors.name = "Full name must be 1-100 characters";
    } else if (nameTrimmed !== (current?.name ?? "")) {
      payload.name = nameTrimmed;
    }

    const usernameTrimmed = editDispUsername.trim();
    if (!usernameTrimmed) {
      errors.username = "Username is required";
    } else if (usernameTrimmed.length > 100) {
      errors.username = "Username must be 1-100 characters";
    } else if (usernameTrimmed !== (current?.username ?? "")) {
      payload.username = usernameTrimmed;
    }

    // Optional: blank = keep current (key omitted entirely).
    if (editDispPassword.length > 0) {
      if (editDispPassword.length < 6) {
        errors.password = "Password must be at least 6 characters";
      } else {
        payload.password = editDispPassword;
      }
    }

    if (Object.keys(errors).length > 0) {
      setEditDispErrors(errors);
      return;
    }

    if (Object.keys(payload).length === 0) {
      // Nothing changed — mirror the customers-tab "no changes" discard.
      const discard = window.confirm("No changes were made. Discard this edit?");
      if (discard) setEditingDispatcherId(null);
      return;
    }

    setEditDispSubmitting(true);
    try {
      await apiFetch<User>(`/api/v1/dispatchers/${editingDispatcherId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setEditingDispatcherId(null);
      loadDispatchers();
      if (onToast) onToast("Dispatcher updated.");
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      // A username collision surfaces at the username field (409 conflict);
      // every other failure keeps the generic form error.
      if (apiErr.status === 409) {
        setEditDispErrors({ username: apiErr.message || "Username already taken" });
      } else {
        setEditDispError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setEditDispSubmitting(false);
    }
  };

  const handleCreateDispatcher = async (e: React.FormEvent) => {
    e.preventDefault();
    setDispErrors({});
    const errors: Record<string, string> = {};
    if (!dispUsername.trim()) errors.username = "Username is required";
    if (dispPassword.length < 6) errors.password = "Password must be at least 6 characters";
    if (!dispName.trim()) errors.name = "Full name is required";

    if (Object.keys(errors).length > 0) {
      setDispErrors(errors);
      return;
    }

    setDispSubmitting(true);
    try {
      await apiFetch<User>("/api/v1/dispatchers", {
        method: "POST",
        body: JSON.stringify({
          username: dispUsername.trim(),
          password: dispPassword,
          name: dispName.trim(),
          phone: dispPhone.trim() || undefined,
        }),
      });
      setShowAddDisp(false);
      setDispUsername("");
      setDispPassword("");
      setDispName("");
      setDispPhone("");
      loadDispatchers();
      if (onToast) onToast("Dispatcher account created.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDispSubmitting(false);
    }
  };

  /**
   * Unit #19 / #20: generic per-row dispatcher flag toggle.
   */
  const handleToggleFlag = async (
    id: string,
    flag: "canCreateJobs" | "canCreateCustomers" | "canCreateProperties" | "canCreateLandfills",
    next: boolean,
    prev: boolean,
    label: string,
  ) => {
    setTogglingDispatcherId(id);
    // Optimistic flip.
    setDispatchers((rows) =>
      rows.map((u) => (u.id === id ? { ...u, [flag]: next } : u)),
    );
    try {
      await apiFetch<User>(`/api/v1/dispatchers/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ [flag]: next }),
      });
      if (onToast) {
        onToast(
          next
            ? `Dispatcher can now create ${label}.`
            : `Dispatcher can no longer create ${label}.`,
        );
      }
    } catch (err: unknown) {
      // Roll back.
      setDispatchers((rows) =>
        rows.map((u) => (u.id === id ? { ...u, [flag]: prev } : u)),
      );
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setTogglingDispatcherId(null);
    }
  };

  // SPEC-75 — Reset Device for a dispatcher. Byte-mirror of the driver
  // handleResetDevice with the drivers-table id swapped for the user id.
  // `flipped` (0|1) distinguishes a real reset from an idempotent re-click;
  // the two toasts MUST NOT collapse into one.
  const handleResetDispatcherDevice = async () => {
    if (!resettingDeviceDispatcher) return;
    setResetDeviceDispatcherSubmitting(true);
    setResetDeviceDispatcherError(null);
    const dispatcher = resettingDeviceDispatcher;
    // Optimistic chip flip → Not bound; server truth reloads on settle below.
    setDispatchers((rows) =>
      rows.map((u) =>
        u.id === dispatcher.id ? { ...u, deviceBound: false, deviceBoundAt: null } : u,
      ),
    );
    try {
      const res = await apiFetch<{
        ok: boolean;
        deviceBound: boolean;
        flipped: number;
      }>(`/api/v1/dispatchers/${dispatcher.id}/reset-device`, { method: "POST" });
      setResettingDeviceDispatcher(null);
      if (onToast) {
        if (res.flipped === 0) onToast("Already unbound — nothing to reset.");
        else onToast("Device reset.");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setResetDeviceDispatcherError(msg);
      if (onToast) onToast(msg, "error");
    } finally {
      setResetDeviceDispatcherSubmitting(false);
      loadDispatchers();
    }
  };

  // SPEC-75 — Reset Password: clears the 3-strike lockout + sets a new
  // password. Also revokes the dispatcher's minted refresh tokens (rv bump):
  // the copy says so, because the dispatcher WILL be signed out at token
  // expiry (audit I1 — the session-death must not surprise the office).
  const handleResetDispatcherPassword = async () => {
    if (!resettingPasswordDispatcher) return;
    setResetPasswordDispatcherError(null);
    if (dispNewPassword.length < 6) {
      setResetPasswordDispatcherError("Password must be at least 6 characters.");
      return;
    }
    if (dispNewPassword !== dispNewPasswordConfirm) {
      setResetPasswordDispatcherError("Passwords do not match.");
      return;
    }
    setResetPasswordDispatcherSubmitting(true);
    try {
      await apiFetch<{ ok: boolean; passwordLocked: boolean }>(
        `/api/v1/dispatchers/${resettingPasswordDispatcher.id}/reset-password`,
        { method: "POST", body: JSON.stringify({ password: dispNewPassword }) },
      );
      setResettingPasswordDispatcher(null);
      if (onToast) {
        onToast(
          "Password reset — account unlocked. The dispatcher is signed out and must log in with the new password.",
        );
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setResetPasswordDispatcherError(msg);
      if (onToast) onToast(msg, "error");
    } finally {
      setResetPasswordDispatcherSubmitting(false);
      loadDispatchers();
    }
  };

  // -- Trucks State ----------------------------------------------------------
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [trucksLoading, setTrucksLoading] = useState(false);
  const [showAddTruck, setShowAddTruck] = useState(false);
  const [truckUnit, setTruckUnit] = useState("");
  const [truckMake, setTruckMake] = useState("");
  const [truckModel, setTruckModel] = useState("");
  const [truckYear, setTruckYear] = useState("");
  const [truckSubmitting, setTruckSubmitting] = useState(false);
  const [editingTruckId, setEditingTruckId] = useState<string | null>(null);
  const [editingSizeId, setEditingSizeId] = useState<string | null>(null);
  const [truckEdit, setTruckEdit] = useState({ unitNumber: "", make: "", model: "", year: "" });
  const [sizeEdit, setSizeEdit] = useState({ yards: "", label: "", sortOrder: "" });
  const [lifecycleSubmitting, setLifecycleSubmitting] = useState<string | null>(null);

  const loadTrucks = () => {
    setTrucksLoading(true);
    apiFetch<Truck[]>("/api/v1/trucks?includeInactive=true")
      .then((res) => {
        setTrucks(res);
        setTrucksLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setTrucksLoading(false);
      });
  };

  const handleCreateTruck = async (e: React.FormEvent) => {
    e.preventDefault();
    const unitNum = parseInt(truckUnit, 10);
    if (isNaN(unitNum) || unitNum <= 0) return;

    setTruckSubmitting(true);
    try {
      const payload: NewTruck = {
        unitNumber: unitNum,
        make: truckMake.trim() || null,
        model: truckModel.trim() || null,
        year: truckYear.trim() ? parseInt(truckYear, 10) : undefined,
      };
      await apiFetch<Truck>("/api/v1/trucks", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setShowAddTruck(false);
      setTruckUnit("");
      setTruckMake("");
      setTruckModel("");
      setTruckYear("");
      loadTrucks();
      if (onToast) onToast("Truck added.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setTruckSubmitting(false);
    }
  };

  // -- Dumpster Sizes State --------------------------------------------------
  const [sizes, setSizes] = useState<DumpsterSize[]>([]);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [showAddSize, setShowAddSize] = useState(false);
  const [sizeYards, setSizeYards] = useState("");
  const [sizeLabel, setSizeLabel] = useState("");
  const [sizeSort, setSizeSort] = useState("0");
  const [sizeSubmitting, setSizeSubmitting] = useState(false);

  const loadSizes = () => {
    setSizesLoading(true);
    apiFetch<DumpsterSize[]>("/api/v1/dumpster-sizes?includeInactive=true")
      .then((res) => {
        setSizes(res);
        setSizesLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setSizesLoading(false);
      });
  };

  const handleCreateSize = async (e: React.FormEvent) => {
    e.preventDefault();
    const yardsNum = parseInt(sizeYards, 10);
    const sortNum = parseInt(sizeSort, 10);
    if (isNaN(yardsNum) || !sizeLabel.trim()) return;

    setSizeSubmitting(true);
    try {
      const payload: NewDumpsterSize = {
        yards: yardsNum,
        label: sizeLabel.trim(),
        sortOrder: isNaN(sortNum) ? 0 : sortNum,
      };
      await apiFetch<DumpsterSize>("/api/v1/dumpster-sizes", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setShowAddSize(false);
      setSizeYards("");
      setSizeLabel("");
      setSizeSort("0");
      loadSizes();
      if (onToast) onToast("Dumpster size added.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSizeSubmitting(false);
    }
  };

  const openEditTruck = (t: Truck) => {
    setEditingTruckId(t.id);
    setTruckEdit({ unitNumber: String(t.unitNumber), make: t.make ?? "", model: t.model ?? "", year: t.year ? String(t.year) : "" });
  };
  const openEditSize = (s: DumpsterSize) => {
    setEditingSizeId(s.id);
    setSizeEdit({ yards: String(s.yards), label: s.label, sortOrder: String(s.sortOrder) });
  };
  const updateTruck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTruckId || lifecycleSubmitting) return;
    setLifecycleSubmitting(editingTruckId);
    try {
      await apiFetch<Truck>(`/api/v1/trucks/${editingTruckId}`, { method: "PATCH", body: JSON.stringify({ unitNumber: Number(truckEdit.unitNumber), make: truckEdit.make || null, model: truckEdit.model || null, year: truckEdit.year ? Number(truckEdit.year) : undefined }) });
      setEditingTruckId(null); loadTrucks();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setLifecycleSubmitting(null); }
  };
  const updateSize = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSizeId || lifecycleSubmitting) return;
    setLifecycleSubmitting(editingSizeId);
    try {
      await apiFetch<DumpsterSize>(`/api/v1/dumpster-sizes/${editingSizeId}`, { method: "PATCH", body: JSON.stringify({ yards: Number(sizeEdit.yards), label: sizeEdit.label, sortOrder: Number(sizeEdit.sortOrder) }) });
      setEditingSizeId(null); loadSizes();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setLifecycleSubmitting(null); }
  };
  const toggleTruck = async (t: Truck) => {
    if (!t.isActive || window.confirm(`Truck #${t.unitNumber} is removed from new-job assignment. Existing jobs and history are unaffected.`)) {
      setLifecycleSubmitting(t.id); try { await apiFetch(`/api/v1/trucks/${t.id}`, { method: "PATCH", body: JSON.stringify({ isActive: !t.isActive }) }); loadTrucks(); } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setLifecycleSubmitting(null); }
    }
  };
  const toggleSize = async (s: DumpsterSize) => {
    if (!s.isActive || window.confirm(`${s.label} is removed from new-job assignment. Existing jobs and history are unaffected.`)) {
      setLifecycleSubmitting(s.id); try { await apiFetch(`/api/v1/dumpster-sizes/${s.id}`, { method: "PATCH", body: JSON.stringify({ isActive: !s.isActive }) }); loadSizes(); } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setLifecycleSubmitting(null); }
    }
  };

  // -- Landfills State -------------------------------------------------------
  const [landfills, setLandfills] = useState<Landfill[]>([]);
  const [landfillsLoading, setLandfillsLoading] = useState(false);
  const [showAddLandfill, setShowAddLandfill] = useState(false);
  const [landfillName, setLandfillName] = useState("");
  const [landfillAddress, setLandfillAddress] = useState("");
  const [landfillSubmitting, setLandfillSubmitting] = useState(false);
  const [editingLandfillId, setEditingLandfillId] = useState<string | null>(null);
  const [landfillEdit, setLandfillEdit] = useState({ name: "", address: "" });
  const [landfillLifecycleSubmitting, setLandfillLifecycleSubmitting] = useState<string | null>(null);

  const loadLandfills = () => {
    setLandfillsLoading(true);
    apiFetch<Landfill[]>("/api/v1/landfills?includeInactive=true")
      .then((res) => {
        setLandfills(res);
        setLandfillsLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setLandfillsLoading(false);
      });
  };

  const handleUpdateLandfill = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLandfillId || !landfillEdit.name.trim()) return;
    setLandfillLifecycleSubmitting(editingLandfillId);
    try {
      await apiFetch<Landfill>(`/api/v1/landfills/${editingLandfillId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: landfillEdit.name, address: landfillEdit.address || null }),
      });
      setEditingLandfillId(null);
      loadLandfills();
      if (onToast) onToast("Landfill updated.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setLandfillLifecycleSubmitting(null); }
  };

  const toggleLandfill = async (landfill: Landfill) => {
    if (landfill.isActive && !window.confirm(`${landfill.name} is removed from new-job assignment. Existing jobs and history are unaffected.`)) return;
    setLandfillLifecycleSubmitting(landfill.id);
    try {
      await apiFetch<Landfill>(`/api/v1/landfills/${landfill.id}`, {
        method: "PATCH", body: JSON.stringify({ isActive: !landfill.isActive }),
      });
      loadLandfills();
      if (onToast) onToast(landfill.isActive ? "Landfill deactivated." : "Landfill reactivated.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setLandfillLifecycleSubmitting(null); }
  };

  const handleCreateLandfill = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!landfillName.trim()) return;

    setLandfillSubmitting(true);
    try {
      const payload: NewLandfill = {
        name: landfillName.trim(),
        address: landfillAddress.trim() || null,
      };
      await apiFetch<Landfill>("/api/v1/landfills", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setShowAddLandfill(false);
      setLandfillName("");
      setLandfillAddress("");
      loadLandfills();
      if (onToast) onToast("Landfill added.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLandfillSubmitting(false);
    }
  };

  // -- Customers & Properties State ------------------------------------------
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  // CUSTOMER-MASTER-DETAIL (PM 2026-09-17): client-side name filter — filters
  // the RENDERED list only; no refetch, no handler/fetch changes.
  const [customerFilter, setCustomerFilter] = useState("");

  // CUSTOMER-MASTER-DETAIL: derived, case-insensitive substring on
  // companyName. Empty/whitespace query ⇒ the full list. Deliberately
  // recomputed per render — memoization adds nothing at this size and the
  // spec pins this exact shape.
  const filteredCustomers = customerFilter.trim()
    ? customers.filter((c) =>
        (c.companyName ?? "").toLowerCase().includes(customerFilter.trim().toLowerCase()),
      )
    : customers;

  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [custCompany, setCustCompany] = useState("");
  const [custContact, setCustContact] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [custLine1, setCustLine1] = useState("");
  const [custCity, setCustCity] = useState("Salinas");
  const [custState] = useState("CA");
  const [custZip, setCustZip] = useState("");
  const [custSubmitting, setCustSubmitting] = useState(false);

  const [showAddProperty, setShowAddProperty] = useState(false);
  const [propSiteName, setPropSiteName] = useState("");
  const [propLine1, setPropLine1] = useState("");
  const [propCity, setPropCity] = useState("Salinas");
  const [propState] = useState("CA");
  const [propZip, setPropZip] = useState("");
  const [propContact, setPropContact] = useState("");
  const [propPhone, setPropPhone] = useState("");
  const [propSubmitting, setPropSubmitting] = useState(false);

  // Edit Customer State (SPEC-44)
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);
  const [editCustomerSubmitting, setEditCustomerSubmitting] = useState(false);
  const [editCustomerError, setEditCustomerError] = useState<string | null>(null);
  const [editCustCompanyName, setEditCustCompanyName] = useState("");
  const [editCustContactName, setEditCustContactName] = useState("");
  const [editCustContactPhone, setEditCustContactPhone] = useState("");
  const [editCustLine1, setEditCustLine1] = useState("");
  const [editCustCity, setEditCustCity] = useState("");
  const [editCustState, setEditCustState] = useState("");
  const [editCustZip, setEditCustZip] = useState("");
  const [editCustActive, setEditCustActive] = useState(true);
  const [editCustReason, setEditCustReason] = useState<CustomerEditReason>("OTHER");

  // Delete Customer State (CUSTOMER-DELETE, PM 2026-09-17) — mirrors the Edit
  // Customer block above; the confirm UI is the same inline expanding form.
  const [deletingCustomerId, setDeletingCustomerId] = useState<string | null>(null);
  const [deleteCustSubmitting, setDeleteCustSubmitting] = useState(false);
  const [deleteCustError, setDeleteCustError] = useState<string | null>(null);
  const [deleteCustReason, setDeleteCustReason] = useState<CustomerEditReason>("OTHER");

  // Edit Property State (SPEC-44)
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
  const [editPropSubmitting, setEditPropSubmitting] = useState(false);
  const [editPropertyError, setEditPropertyError] = useState<string | null>(null);
  const [editPropSiteName, setEditPropSiteName] = useState("");
  const [editPropLine1, setEditPropLine1] = useState("");
  const [editPropCity, setEditPropCity] = useState("");
  const [editPropState, setEditPropState] = useState("");
  const [editPropZip, setEditPropZip] = useState("");
  const [editPropContactName, setEditPropContactName] = useState("");
  const [editPropContactPhone, setEditPropContactPhone] = useState("");
  const [editPropActive, setEditPropActive] = useState(true);
  const [editPropReason, setEditPropReason] = useState<PropertyEditReason>("OTHER");

  // Toggle Property Active State (PROPERTY-INACTIVATE, PM 2026-09-17) —
  // mirrors the Edit Property block; the confirm UI is the same inline
  // expanding form pattern.
  const [togglingPropertyId, setTogglingPropertyId] = useState<string | null>(null);
  const [togglingPropertyTarget, setTogglingPropertyTarget] = useState<"inactivate" | "reactivate">("inactivate");
  const [togglePropSubmitting, setTogglePropSubmitting] = useState(false);
  const [togglePropError, setTogglePropError] = useState<string | null>(null);
  const [togglePropReason, setTogglePropReason] = useState<PropertyEditReason>("OTHER");

  const loadCustomers = () => {
    setCustomersLoading(true);
    apiFetch<Customer[]>("/api/v1/customers")
      .then((res) => {
        setCustomers(res);
        setCustomersLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setCustomersLoading(false);
      });
  };

  const loadProperties = (customerId: string) => {
    setPropertiesLoading(true);
    // PROPERTY-INACTIVATE (PM 2026-09-17): Admin must see (and reactivate)
    // inactive sites — the only consumer that passes the flag.
    apiFetch<Property[]>(
      `/api/v1/customers/${customerId}/properties?includeInactive=true`,
    )
      .then((res) => {
        setProperties(res);
        setPropertiesLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setPropertiesLoading(false);
      });
  };

  useEffect(() => {
    if (selectedCustomerId) {
      loadProperties(selectedCustomerId);
    } else {
      setProperties([]);
    }
    setEditingPropertyId(null);
    setEditPropertyError(null);
    setEditPropSubmitting(false);
    setEditingCustomerId(null);
    setEditCustomerError(null);
    setEditCustomerSubmitting(false);
  }, [selectedCustomerId]);

  const openEditCustomer = (c: Customer) => {
    setShowAddCustomer(false);
    setShowAddProperty(false);
    setEditingPropertyId(null);
    setEditingCustomerId(c.id);
    setEditCustCompanyName(c.companyName ?? "");
    setEditCustContactName(c.contactName ?? "");
    setEditCustContactPhone(c.contactPhone ?? "");
    setEditCustLine1(c.billingAddressLine1 ?? "");
    setEditCustCity(c.billingCity ?? "");
    setEditCustState(c.billingState ?? "");
    setEditCustZip(c.billingZip ?? "");
    setEditCustActive(c.isActive);
    setEditCustReason("OTHER");
    setEditCustomerError(null);
  };

  const openEditProperty = (p: Property) => {
    setShowAddCustomer(false);
    setShowAddProperty(false);
    setEditingCustomerId(null);
    setEditingPropertyId(p.id);
    setEditPropSiteName(p.siteName ?? "");
    setEditPropLine1(p.addressLine1 ?? "");
    setEditPropCity(p.city ?? "");
    setEditPropState(p.state ?? "");
    setEditPropZip(p.zip ?? "");
    setEditPropContactName(p.contactName ?? "");
    setEditPropContactPhone(p.contactPhone ?? "");
    setEditPropActive(p.isActive);
    setEditPropReason("OTHER");
    setEditPropertyError(null);
  };

  const handleUpdateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCustomerId) return;
    const currentCust = customers.find((c) => c.id === editingCustomerId);
    if (!currentCust) {
      setEditCustomerError("Customer no longer available. Please refresh and try again.");
      return;
    }

    if (
      !editCustCompanyName.trim() ||
      !editCustLine1.trim() ||
      !editCustCity.trim() ||
      !editCustState.trim() ||
      !editCustZip.trim()
    ) {
      setEditCustomerError("Fill all required fields");
      return;
    }

    const fieldsUnchanged =
      editCustCompanyName.trim() === (currentCust.companyName ?? "").trim() &&
      editCustContactName.trim() === (currentCust.contactName ?? "").trim() &&
      editCustContactPhone.trim() === (currentCust.contactPhone ?? "").trim() &&
      editCustLine1.trim() === (currentCust.billingAddressLine1 ?? "").trim() &&
      editCustCity.trim() === (currentCust.billingCity ?? "").trim() &&
      editCustState.trim() === (currentCust.billingState ?? "").trim() &&
      editCustZip.trim() === (currentCust.billingZip ?? "").trim() &&
      editCustActive === currentCust.isActive;

    const reasonUnchanged = editCustReason === "OTHER";

    if (fieldsUnchanged && reasonUnchanged) {
      const discard = window.confirm("No changes were made. Discard this edit?");
      if (discard) {
        setEditingCustomerId(null);
        setEditCustomerError(null);
      }
      return;
    }

    if (editCustActive === false && currentCust.isActive === true) {
      const ok = window.confirm(
        "Deactivate this customer? It will be hidden from active lists and new jobs cannot be created for it. Existing jobs are unaffected.",
      );
      if (!ok) return;
    }

    setEditCustomerSubmitting(true);
    try {
      const payload = {
        companyName: editCustCompanyName.trim(),
        billingAddressLine1: editCustLine1.trim(),
        billingCity: editCustCity.trim(),
        billingState: editCustState.trim(),
        billingZip: editCustZip.trim(),
        contactName: editCustContactName.trim() || null,
        contactPhone: editCustContactPhone.trim() || null,
        isActive: editCustActive,
        reason: editCustReason,
      };
      await apiFetch<Customer>(`/api/v1/customers/${editingCustomerId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setEditingCustomerId(null);
      setEditCustomerError(null);
      loadCustomers();
      if (onToast) onToast("Customer updated.");
    } catch (err: unknown) {
      setEditCustomerError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditCustomerSubmitting(false);
    }
  };

  // CUSTOMER-DELETE (PM 2026-09-17): mirrors handleUpdateCustomer's shape —
  // confirm gate → apiFetch → close → list refresh → toast, error state on
  // failure. "Optimistic drop" here is the list refresh on settle (same as
  // every other admin mutation in this file — no local array surgery).
  const handleDeleteCustomer = async () => {
    if (!deletingCustomerId || deleteCustSubmitting) return;
    setDeleteCustSubmitting(true);
    try {
      await apiFetch<{ ok: boolean; deletedProperties: number }>(
        `/api/v1/customers/${deletingCustomerId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: deleteCustReason }),
        },
      );
      setDeletingCustomerId(null);
      setDeleteCustError(null);
      setDeleteCustReason("OTHER");
      loadCustomers();
      if (onToast) onToast("Customer deleted.");
    } catch (err: unknown) {
      setDeleteCustError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleteCustSubmitting(false);
    }
  };

  const handleUpdateProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPropertyId) return;
    const currentProp = properties.find((p) => p.id === editingPropertyId);
    if (!currentProp) {
      setEditPropertyError("Site no longer available. Please refresh and try again.");
      return;
    }

    if (
      !editPropSiteName.trim() ||
      !editPropLine1.trim() ||
      !editPropCity.trim() ||
      !editPropState.trim() ||
      !editPropZip.trim()
    ) {
      setEditPropertyError("Fill all required fields");
      return;
    }

    const fieldsUnchanged =
      editPropSiteName.trim() === (currentProp.siteName ?? "").trim() &&
      editPropLine1.trim() === (currentProp.addressLine1 ?? "").trim() &&
      editPropCity.trim() === (currentProp.city ?? "").trim() &&
      editPropState.trim() === (currentProp.state ?? "").trim() &&
      editPropZip.trim() === (currentProp.zip ?? "").trim() &&
      editPropContactName.trim() === (currentProp.contactName ?? "").trim() &&
      editPropContactPhone.trim() === (currentProp.contactPhone ?? "").trim() &&
      editPropActive === currentProp.isActive;

    const reasonUnchanged = editPropReason === "OTHER";

    if (fieldsUnchanged && reasonUnchanged) {
      const discard = window.confirm("No changes were made. Discard this edit?");
      if (discard) {
        setEditingPropertyId(null);
        setEditPropertyError(null);
      }
      return;
    }

    if (editPropActive === false && currentProp.isActive === true) {
      const ok = window.confirm(
        "Deactivate this site? It will be hidden from the active property list and cannot be selected for new jobs. Existing jobs are unaffected.",
      );
      if (!ok) return;
    }

    setEditPropSubmitting(true);
    try {
      const payload = {
        siteName: editPropSiteName.trim(),
        addressLine1: editPropLine1.trim(),
        city: editPropCity.trim(),
        state: editPropState.trim(),
        zip: editPropZip.trim(),
        contactName: editPropContactName.trim() || null,
        contactPhone: editPropContactPhone.trim() || null,
        isActive: editPropActive,
        reason: editPropReason,
      };
      await apiFetch<Property>(`/api/v1/properties/${editingPropertyId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setEditingPropertyId(null);
      setEditPropertyError(null);
      if (selectedCustomerId) loadProperties(selectedCustomerId);
      if (onToast) onToast("Site updated.");
    } catch (err: unknown) {
      setEditPropertyError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditPropSubmitting(false);
    }
  };

  // PROPERTY-INACTIVATE (PM 2026-09-17): mirrors handleUpdateProperty's
  // shape — apiFetch POST {reason} → close → loadProperties refresh → toast,
  // error state on failure. `selectedCustomerId` is captured by the card
  // render; the guard keeps a stale confirm (customer switched mid-flight)
  // from posting against the wrong list.
  const handleToggleProperty = async () => {
    if (!togglingPropertyId || togglePropSubmitting) return;
    if (!selectedCustomerId) {
      setTogglePropError("No customer selected. Please refresh and try again.");
      return;
    }
    setTogglePropSubmitting(true);
    try {
      await apiFetch<{ ok: boolean; property: Property }>(
        `/api/v1/properties/${togglingPropertyId}/${togglingPropertyTarget}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: togglePropReason }),
        },
      );
      setTogglingPropertyId(null);
      setTogglePropError(null);
      setTogglePropReason("OTHER");
      loadProperties(selectedCustomerId);
      if (onToast) {
        onToast(
          togglingPropertyTarget === "inactivate"
            ? "Site inactivated — removed from job site dropdowns."
            : "Site reactivated.",
        );
      }
    } catch (err: unknown) {
      setTogglePropError(err instanceof Error ? err.message : String(err));
    } finally {
      setTogglePropSubmitting(false);
    }
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!custCompany.trim() || !custLine1.trim() || !custCity.trim() || !custZip.trim()) return;

    setCustSubmitting(true);
    try {
      const payload: NewCustomer = {
        companyName: custCompany.trim(),
        billingAddressLine1: custLine1.trim(),
        billingCity: custCity.trim(),
        billingState: custState.trim(),
        billingZip: custZip.trim(),
        contactName: custContact.trim() || null,
        contactPhone: custPhone.trim() || null,
      };
      const created = await apiFetch<Customer>("/api/v1/customers", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setShowAddCustomer(false);
      setCustCompany("");
      setCustContact("");
      setCustPhone("");
      setCustLine1("");
      setCustZip("");
      loadCustomers();
      setSelectedCustomerId(created.id);
      if (onToast) onToast("Customer created. Add at least one property for job dispatch.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCustSubmitting(false);
    }
  };

  const handleCreateProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomerId || !propSiteName.trim() || !propLine1.trim() || !propCity.trim() || !propZip.trim()) {
      return;
    }

    setPropSubmitting(true);
    try {
      const payload: NewProperty = {
        customerId: selectedCustomerId,
        siteName: propSiteName.trim(),
        addressLine1: propLine1.trim(),
        city: propCity.trim(),
        state: propState.trim(),
        zip: propZip.trim(),
        contactName: propContact.trim() || null,
        contactPhone: propPhone.trim() || null,
      };
      await apiFetch<Property>(`/api/v1/customers/${selectedCustomerId}/properties`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setShowAddProperty(false);
      setPropSiteName("");
      setPropLine1("");
      setPropZip("");
      setPropContact("");
      setPropPhone("");
      loadProperties(selectedCustomerId);
      if (onToast) onToast("Property site added.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPropSubmitting(false);
    }
  };

  // -- Tenant Settings State (TAB 7, Unit #18) ----------------------------
  const [tenantCfg, setTenantCfg] = useState<Tenant | null>(null);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantSaving, setTenantSaving] = useState(false);
  const [tenantFieldErrors, setTenantFieldErrors] = useState<
    Record<string, string>
  >({});

  const [phoneDraft, setPhoneDraft] = useState("");
  // DISPATCH-PHONE (PM 2026-09-17): Dispatcher Default Phone draft.
  const [dispatchPhoneDraft, setDispatchPhoneDraft] = useState("");
  const [retentionDraft, setRetentionDraft] = useState("");
  const [retentionClear, setRetentionClear] = useState(false);
  const [erReturnDraft, setErReturnDraft] = useState("");
  const [doBillableDollarsDraft, setDoBillableDollarsDraft] = useState("");

  const [purgePreview, setPurgePreview] = useState<number | null>(null);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeResult, setPurgeResult] = useState<PurgeResult | null>(null);
  const [purgeConfirmStep, setPurgeConfirmStep] = useState(0);
  const [orphanConfirmStep, setOrphanConfirmStep] = useState(0);
  const [purgeRunning, setPurgeRunning] = useState(false);

  const loadTenant = () => {
    setTenantLoading(true);
    apiFetch<Tenant>("/api/v1/tenant")
      .then((res) => {
        setTenantCfg(res);
        setTenantLoading(false);
        setPhoneDraft(res.phone ?? "");
        setDispatchPhoneDraft(res.dispatcherDefaultPhone ?? "");
        setRetentionClear(res.photoRetentionMonths === null);
        setRetentionDraft(
          res.photoRetentionMonths === null ? "" : String(res.photoRetentionMonths),
        );
        setErReturnDraft(String(res.erReturnMinutes));
        setDoBillableDollarsDraft(
          (res.defaultDoBillableCents / 100).toFixed(2),
        );
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setTenantLoading(false);
      });
  };

  const loadPurgePreview = () => {
    setPurgeLoading(true);
    apiFetch<PurgePreview>("/api/v1/photos/purge-preview")
      .then((res) => {
        setPurgePreview(res.purgeable);
        setPurgeLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setPurgeLoading(false);
      });
  };

  const [togglingNfc, setTogglingNfc] = useState(false);

  // ── Security Keys (WebAuthn / YubiKey) State ──────────────────────────────
  const [credentials, setCredentials] = useState<WebauthnCredentialInfo[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [togglingOfficeKey, setTogglingOfficeKey] = useState(false);
  const [registeringKey, setRegisteringKey] = useState(false);
  const [keyLabelDraft, setKeyLabelDraft] = useState("");
  const [showKeyLabelModal, setShowKeyLabelModal] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);

  const loadCredentials = () => {
    setCredentialsLoading(true);
    apiFetch<WebauthnCredentialsListResponse>("/api/v1/webauthn/credentials")
      .then((res) => {
        setCredentials(res.credentials);
        setTenantCfg((cur) =>
          cur ? { ...cur, officeKeyRequired: res.officeKeyRequired } : null,
        );
        setCredentialsLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setCredentialsLoading(false);
      });
  };

  const handleToggleOfficeKeyRequired = async (next: boolean, prev: boolean) => {
    setTogglingOfficeKey(true);
    // Optimistic flip
    setTenantCfg((cur) => (cur ? { ...cur, officeKeyRequired: next } : null));
    try {
      const updated = await apiFetch<Tenant>("/api/v1/tenant", {
        method: "PATCH",
        body: JSON.stringify({ officeKeyRequired: next }),
      });
      setTenantCfg(updated);
      if (onToast) {
        onToast(
          next
            ? "Security key requirement enabled for office login."
            : "Security key requirement disabled — password-only login active.",
        );
      }
    } catch (err: unknown) {
      // Roll back
      setTenantCfg((cur) => (cur ? { ...cur, officeKeyRequired: prev } : null));
      const msg = err instanceof Error ? err.message : String(err);
      if (onToast) {
        onToast(msg, "error");
      } else {
        setError(msg);
      }
    } finally {
      setTogglingOfficeKey(false);
    }
  };

  const handleStartRegisterKey = () => {
    const defaultLabel = `Key ${credentials.length + 1}`;
    setKeyLabelDraft(defaultLabel);
    setShowKeyLabelModal(true);
  };

  const handleExecuteRegisterKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowKeyLabelModal(false);
    setRegisteringKey(true);
    try {
      // 1. Begin registration
      const beginRes = await apiFetch<WebauthnRegisterBeginResponse>(
        "/api/v1/webauthn/register/begin",
        { method: "POST" },
      );

      // 2. Browser ceremony
      const assertion = await executeWebAuthnRegistration(beginRes.options as never);

      // 3. Finish registration
      await apiFetch<{ verified: boolean; credential: unknown }>(
        "/api/v1/webauthn/register/finish",
        {
          method: "POST",
          body: JSON.stringify({
            challengeToken: beginRes.challengeToken,
            response: assertion,
            label: keyLabelDraft.trim() || undefined,
          }),
        },
      );

      loadCredentials();
      if (onToast) onToast("Security key registered successfully.");
    } catch (err: unknown) {
      console.error("[webauthn/register]", err);
      const msg = mapWebAuthnError(err);
      if (onToast) {
        onToast(msg, "error");
      } else {
        setError(msg);
      }
    } finally {
      setRegisteringKey(false);
    }
  };

  const handleDeleteKey = async (cred: WebauthnCredentialInfo) => {
    const ok = window.confirm(
      `Remove security key "${cred.label || cred.id}"?`,
    );
    if (!ok) return;

    setDeletingKeyId(cred.id);
    try {
      await apiFetch<{ ok: boolean }>(`/api/v1/webauthn/credentials/${cred.id}`, {
        method: "DELETE",
      });
      loadCredentials();
      if (onToast) onToast("Security key removed.");
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      const msg = apiErr.message || (err instanceof Error ? err.message : String(err));
      if (onToast) {
        onToast(msg, "error");
      } else {
        setError(msg);
      }
    } finally {
      setDeletingKeyId(null);
    }
  };

  // ── Timeclock NFC Tags State (MOVED FROM TIMECLOCK TAB) ────────────────────
  const [nfcTags, setNfcTags] = useState<TimeclockTag[]>([]);
  const [nfcTagsLoading, setNfcTagsLoading] = useState(false);
  const [showRegTag, setShowRegTag] = useState(false);
  const [regUid, setRegUid] = useState("");
  const [regLabel, setRegLabel] = useState("");
  const [regErrors, setRegErrors] = useState<Record<string, string>>({});
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [regError, setRegError] = useState<string | null>(null);

  useEffect(() => {
    setRegErrors({});
    setRegError(null);
  }, [showRegTag]);

  const [renamingTag, setRenamingTag] = useState<TimeclockTag | null>(null);
  const [renameLabel, setRenameLabel] = useState("");
  const [renameErrors, setRenameErrors] = useState<Record<string, string>>({});
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [togglingTagId, setTogglingTagId] = useState<string | null>(null);

  useEffect(() => {
    if (renamingTag) {
      setRenameLabel(renamingTag.label);
    }
    setRenameErrors({});
    setRenameError(null);
  }, [renamingTag]);

  const loadNfcTags = () => {
    setNfcTagsLoading(true);
    apiFetch<TimeclockTag[]>("/api/v1/timeclock/tags")
      .then((res) => {
        setNfcTags(res);
        setNfcTagsLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setNfcTagsLoading(false);
      });
  };

  const handleRegisterTag = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegErrors({});
    setRegError(null);
    const errors: Record<string, string> = {};

    let normalizedUid = "";
    try {
      normalizedUid = normalizeUidClient(regUid);
    } catch (err: unknown) {
      errors.uid = err instanceof Error ? err.message : String(err);
    }

    const labelTrimmed = regLabel.trim();
    if (!labelTrimmed) {
      errors.label = "Label must be 1–100 characters";
    } else if (labelTrimmed.length > 100) {
      errors.label = "Label must be 1–100 characters";
    }

    if (Object.keys(errors).length > 0) {
      setRegErrors(errors);
      return;
    }

    setRegSubmitting(true);
    try {
      await apiFetch<TimeclockTag>("/api/v1/timeclock/tags", {
        method: "POST",
        body: JSON.stringify({ uid: normalizedUid, label: labelTrimmed }),
      });
      setShowRegTag(false);
      setRegUid("");
      setRegLabel("");
      loadNfcTags();
      if (onToast) onToast("Tag registered.");
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.code === "conflict" && typeof apiErr.message === "string") {
        setRegErrors({ uid: apiErr.message });
      } else if (typeof apiErr.fieldErrors === "object" && Object.keys(apiErr.fieldErrors).length > 0) {
        setRegErrors(apiErr.fieldErrors);
      } else {
        setRegError(apiErr.message || (err instanceof Error ? err.message : String(err)));
      }
    } finally {
      setRegSubmitting(false);
    }
  };

  const handleRenameTag = async (e: React.FormEvent) => {
    e.preventDefault();
    setRenameErrors({});
    setRenameError(null);

    if (!renamingTag) return;

    const labelTrimmed = renameLabel.trim();
    if (!labelTrimmed || labelTrimmed.length > 100) {
      setRenameErrors({ label: "Label must be 1–100 characters" });
      return;
    }

    setRenameSubmitting(true);
    try {
      await apiFetch<TimeclockTag>(`/api/v1/timeclock/tags/${renamingTag.id}`, {
        method: "PATCH",
        body: JSON.stringify({ label: labelTrimmed }),
      });
      setRenamingTag(null);
      loadNfcTags();
      if (onToast) onToast("Tag renamed.");
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.code === "conflict" && typeof apiErr.message === "string") {
        setRenameErrors({ label: apiErr.message });
      } else if (typeof apiErr.fieldErrors === "object" && Object.keys(apiErr.fieldErrors).length > 0) {
        setRenameErrors(apiErr.fieldErrors);
      } else {
        setRenameError(apiErr.message || (err instanceof Error ? err.message : String(err)));
      }
    } finally {
      setRenameSubmitting(false);
    }
  };

  const handleToggleTagActive = async (t: TimeclockTag) => {
    setTogglingTagId(t.id);
    try {
      await apiFetch<TimeclockTag>(`/api/v1/timeclock/tags/${t.id}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !t.isActive }),
      });
      loadNfcTags();
      if (onToast) {
        onToast(t.isActive ? "Tag deactivated." : "Tag reactivated.");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (onToast) {
        onToast(msg, "error");
      } else {
        setError(msg);
      }
    } finally {
      setTogglingTagId(null);
    }
  };

  const handleToggleNfcRequired = async (next: boolean, prev: boolean) => {
    setTogglingNfc(true);
    // Optimistic flip
    setTenantCfg((cur) => (cur ? { ...cur, timeclockNfcRequired: next } : null));
    try {
      const updated = await apiFetch<Tenant>("/api/v1/tenant", {
        method: "PATCH",
        body: JSON.stringify({ timeclockNfcRequired: next }),
      });
      setTenantCfg(updated);
      if (onToast) {
        onToast(
          next
            ? "NFC tag requirement enabled for timeclock punches."
            : "NFC tag requirement disabled — testing mode active (NO_TAG punches).",
        );
      }
    } catch (err: unknown) {
      // Roll back
      setTenantCfg((cur) => (cur ? { ...cur, timeclockNfcRequired: prev } : null));
      const msg = err instanceof Error ? err.message : String(err);
      if (onToast) {
        onToast(msg, "error");
      } else {
        setError(msg);
      }
    } finally {
      setTogglingNfc(false);
    }
  };

  // SPEC-68 §4.16 — passkey policy toggle beside the NFC toggle. Chip text
  // deliberately NOT "Testing Mode" (that string belongs to NFC).
  const [togglingPasskeysRequired, setTogglingPasskeysRequired] = useState(false);

  const handleTogglePasskeysRequired = async (next: boolean, prev: boolean) => {
    setTogglingPasskeysRequired(true);
    // Optimistic flip
    setTenantCfg((cur) => (cur ? { ...cur, passkeysRequired: next } : null));
    try {
      const updated = await apiFetch<Tenant>("/api/v1/tenant", {
        method: "PATCH",
        body: JSON.stringify({ passkeysRequired: next }),
      });
      setTenantCfg(updated);
      if (onToast) {
        onToast(
          next
            ? "Passkey enforcement enabled — enrollment required at next NFC punch-in."
            : "Passkey enforcement disabled — PIN/password logins mint full sessions.",
        );
      }
    } catch (err: unknown) {
      // Roll back
      setTenantCfg((cur) => (cur ? { ...cur, passkeysRequired: prev } : null));
      const msg = err instanceof Error ? err.message : String(err);
      if (onToast) {
        onToast(msg, "error");
      } else {
        setError(msg);
      }
    } finally {
      setTogglingPasskeysRequired(false);
    }
  };

  // SPEC-67 §3.7 break-glass #2 — time-boxed tenant-wide binding toggle.
  // The rendered predicate comes from `deviceBindingEnforced` (server-derived
  // from Postgres now()) — NEVER recomputed from a client clock. Disabling
  // sends the window; re-enabling sends `null` (JSON `"key": null` = distinct
  // API meaning from omitting the key, which is "no change").
  const [togglingDeviceBinding, setTogglingDeviceBinding] = useState(false);
  const [deviceBindingHours, setDeviceBindingHours] = useState<number>(4);

  const handleToggleDeviceBinding = async (disable: boolean, prev: Tenant | null) => {
    setTogglingDeviceBinding(true);
    // Optimistic flip — boolean only. The disabled-until INSTANT is never
    // fabricated client-side (SPEC-67 §3.7: the predicate lives server-side
    // on Postgres now()); the server response restores the real instant.
    setTenantCfg((cur) =>
      cur ? { ...cur, deviceBindingEnforced: !disable } : null,
    );
    try {
      const updated = await apiFetch<Tenant>("/api/v1/tenant", {
        method: "PATCH",
        body: JSON.stringify(
          disable
            ? { deviceBindingDisabledHours: deviceBindingHours }
            : { deviceBindingDisabledHours: null },
        ),
      });
      setTenantCfg(updated);
      if (onToast) {
        onToast(
          disable
            ? `Device binding disabled for ${deviceBindingHours} h — restores automatically.`
            : "Device binding enforcement restored.",
        );
      }
    } catch (err: unknown) {
      // Roll back to the caller's captured previous state.
      setTenantCfg(prev);
      const msg = err instanceof Error ? err.message : String(err);
      if (onToast) {
        onToast(msg, "error");
      } else {
        setError(msg);
      }
    } finally {
      setTogglingDeviceBinding(false);
    }
  };

  const handleSaveTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setTenantFieldErrors({});
    const errors: Record<string, string> = {};

    const phoneTrimmed = phoneDraft.trim();
    if (phoneTrimmed.length > 0) {
      if (!/^(?=.*\d)[\d\s\-()+]{7,20}$/.test(phoneTrimmed)) {
        errors.phone = "Phone must be 7-20 chars: digits, spaces, dashes, parens, or +";
      }
    }

    // DISPATCH-PHONE (PM 2026-09-17): same shape discipline as Office Phone.
    const dispatchPhoneTrimmed = dispatchPhoneDraft.trim();
    if (dispatchPhoneTrimmed.length > 0) {
      if (!/^(?=.*\d)[\d\s\-()+]{7,20}$/.test(dispatchPhoneTrimmed)) {
        errors.dispatcherDefaultPhone =
          "Phone must be 7-20 chars: digits, spaces, dashes, parens, or +";
      }
    }

    let retentionParsed: number | null = null;
    if (!retentionClear) {
      if (retentionDraft.trim() === "") {
        errors.photoRetentionMonths =
          "Enter 0 or more months, or check 'No limit' to disable purge";
      } else {
        const n = Number(retentionDraft);
        if (!Number.isInteger(n) || n < 0 || n > 600) {
          errors.photoRetentionMonths = "Retention must be 0-600 months";
        } else {
          retentionParsed = n;
        }
      }
    }

    const erN = Number(erReturnDraft);
    if (!Number.isInteger(erN) || erN < 1 || erN > 1440) {
      errors.erReturnMinutes = "Must be 1-1440 (1 min to 24 hours)";
    }

    const doCents = parseDollarsToCents(doBillableDollarsDraft.trim());
    if (
      !doBillableDollarsDraft.trim() ||
      isNaN(doCents) ||
      doCents < 0
    ) {
      errors.defaultDoBillableCents = "Must be $0.00 or more";
    } else if (
      !Number.isFinite(doCents) ||
      !Number.isSafeInteger(doCents) ||
      doCents > 99_999_999
    ) {
      errors.defaultDoBillableCents = "Amount too large";
    } else if (doCents / 100 > MAX_TONS) {
      errors.defaultDoBillableCents = "Amount too large";
    }

    if (Object.keys(errors).length > 0) {
      setTenantFieldErrors(errors);
      return;
    }

    setTenantSaving(true);
    try {
      const payload: Record<string, unknown> = {
        phone: phoneTrimmed.length > 0 ? phoneTrimmed : null,
        dispatcherDefaultPhone:
          dispatchPhoneTrimmed.length > 0 ? dispatchPhoneTrimmed : null,
        photoRetentionMonths: retentionClear ? null : retentionParsed,
        erReturnMinutes: erN,
        defaultDoBillableCents: doCents,
      };
      const updated = await apiFetch<Tenant>("/api/v1/tenant", {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setTenantCfg(updated);
      if (onToast) onToast("Tenant settings saved.");
      void loadPurgePreview();
    } catch (err: unknown) {
      if (
        err &&
        typeof err === "object" &&
        "fieldErrors" in (err as { fieldErrors?: Record<string, string> })
      ) {
        const fe = (err as { fieldErrors: Record<string, string> }).fieldErrors;
        setTenantFieldErrors(fe);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setTenantSaving(false);
    }
  };

  const handlePurge = async (orphansOnly: boolean) => {
    if (!orphansOnly && purgeConfirmStep !== 1) {
      setPurgeConfirmStep(1);
      return;
    }
    if (orphansOnly && orphanConfirmStep !== 1) {
      setOrphanConfirmStep(1);
      return;
    }
    setPurgeRunning(true);
    try {
      const res = await apiFetch<PurgeResult>("/api/v1/photos/purge", {
        method: "POST",
        body: JSON.stringify({ confirm: true, orphansOnly }),
      });
      setPurgeResult(res);
      if (onToast) {
        onToast(
          orphansOnly
            ? `Cleared ${res.purged} orphan R2 object(s).`
            : `Purged ${res.purged} photo(s).`,
        );
      }
      setPurgeConfirmStep(0);
      setOrphanConfirmStep(0);
      void loadPurgePreview();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPurgeRunning(false);
    }
  };

  // Initial load on tab change
  useEffect(() => {
    setError(null);
    if (activeTab === "drivers") loadDrivers();
    if (activeTab === "dispatchers") loadDispatchers();
    if (activeTab === "trucks") loadTrucks();
    if (activeTab === "sizes") loadSizes();
    if (activeTab === "landfills") loadLandfills();
    if (activeTab === "customers") loadCustomers();
    if (activeTab === "settings") {
      loadTenant();
      loadCredentials();
      loadNfcTags();
      loadPurgePreview();
    }
  }, [activeTab]);

  return (
    <main className="surface office-surface">
      <header className="surface-header">
        <h1>Operations Admin</h1>
        <p className="sub">Manage system personnel, reference data, and configuration</p>
      </header>

      {/* Admin Sub-Tabs */}
      <div className="admin-subtabs">
        <button
          type="button"
          className={`admin-subtab ${activeTab === "drivers" ? "active" : ""}`}
          onClick={() => setActiveTab("drivers")}
        >
          Drivers
        </button>
        <button
          type="button"
          className={`admin-subtab ${activeTab === "dispatchers" ? "active" : ""}`}
          onClick={() => setActiveTab("dispatchers")}
        >
          Dispatchers
        </button>
        <button
          type="button"
          className={`admin-subtab ${activeTab === "trucks" ? "active" : ""}`}
          onClick={() => setActiveTab("trucks")}
        >
          Trucks
        </button>
        <button
          type="button"
          className={`admin-subtab ${activeTab === "sizes" ? "active" : ""}`}
          onClick={() => setActiveTab("sizes")}
        >
          Dumpster Sizes
        </button>
        <button
          type="button"
          className={`admin-subtab ${activeTab === "landfills" ? "active" : ""}`}
          onClick={() => setActiveTab("landfills")}
        >
          Landfills
        </button>
        <button
          type="button"
          className={`admin-subtab ${activeTab === "customers" ? "active" : ""}`}
          onClick={() => setActiveTab("customers")}
        >
          Customers &amp; Sites
        </button>
        <button
          type="button"
          className={`admin-subtab ${activeTab === "settings" ? "active" : ""}`}
          onClick={() => setActiveTab("settings")}
        >
          Tenant Settings
        </button>
      </div>

      {error && (
        <div className="banner banner-danger" style={{ marginBottom: "16px" }}>
          {error}
        </div>
      )}

      {/* TAB 1: Drivers */}
      {activeTab === "drivers" && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2>Driver Fleet</h2>
            <Button onClick={() => setShowAddDriver(true)}>+ Add Driver</Button>
          </div>

          {showAddDriver && (
            <form onSubmit={handleCreateDriver} className="admin-inline-form">
              <h3>New Driver</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="dName">Full Name*</label>
                  <input
                    id="dName"
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    placeholder="e.g. Jesus"
                    required
                  />
                  <FieldError message={driverFieldErrors.name} />
                </div>
                <div className="form-group">
                  <label htmlFor="dPhone">Phone Number</label>
                  <input
                    id="dPhone"
                    value={driverPhone}
                    onChange={(e) => setDriverPhone(e.target.value)}
                    placeholder="e.g. 831-555-0101"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dPin">4-Digit PIN*</label>
                  <input
                    id="dPin"
                    maxLength={4}
                    value={driverPin}
                    onChange={(e) => setDriverPin(e.target.value)}
                    placeholder="e.g. 1234"
                    required
                  />
                  <FieldError message={driverFieldErrors.pin} />
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button type="submit" disabled={driverSubmitting}>
                  {driverSubmitting ? "Creating…" : "Save Driver"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAddDriver(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {driversLoading ? (
            <p className="muted">Loading drivers…</p>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>Device</th>
                    <th>Passkey</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((d) => (
                    <tr key={d.id} style={!d.isActive ? { opacity: 0.55 } : undefined}>
                      <td><strong>{d.name}</strong></td>
                      <td>{d.phone || "—"}</td>
                      <td>
                        {!d.isActive ? <span className="tag tag-red">Removed</span> : (
                          <span className={`tag ${d.isAvailable ? "tag-green" : "tag-yellow"}`}>
                            {d.isAvailable ? "Available" : "On Job"}
                          </span>
                        )}
                      </td>
                      {/* SPEC-67 §4: binding chip — display-only, driven by the
                          wire fields. No route trusts this; enforcement
                          re-reads device_sessions server-side. */}
                      <td>
                        {d.deviceBound ? (
                          <span className="tag tag-green" title={formatLocalDate(d.deviceBoundAt)}>
                            Bound{d.deviceBoundAt ? ` ${formatLocalDate(d.deviceBoundAt)}` : ""}
                          </span>
                        ) : (
                          <span className="tag">Not bound</span>
                        )}
                      </td>
                      {/* SPEC-68 §4.14: passkey status chip — display-only,
                          driven by the wire fields from GET /drivers. No route
                          trusts this; gates re-read webauthn_credentials. The
                          PIN-only chip mirrors §4.14's fallback disclosure. */}
                      <td>
                        {d.hasPasskey ? (
                          <>
                            <span className="tag tag-green">
                              Enrolled ({d.passkeySynced === true ? "synced" : "device"})
                            </span>
                            {d.passkeyFallback && (
                              <span className="tag tag-yellow">PIN-only login</span>
                            )}
                          </>
                        ) : (
                          <span className="tag">Not set</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "6px" }}>
                          {d.isActive && <>
                            <Button variant="secondary" onClick={() => {
                              setEditingDriverId(d.id);
                              setEditDriverName(d.name);
                              setEditDriverPhone(d.phone || "");
                              setEditDriverPin("");
                            }}>Edit</Button>
                            <Button variant="ghost" onClick={() => handleUpdateDriver(d.id, true)}>Unlock PIN</Button>
                            <Button variant="ghost" onClick={() => { setResettingDriver(d); setResetDeviceError(null); }}>Reset Device</Button>
                            <Button variant="ghost" onClick={() => { setResettingPasskeyDriver(d); setResetPasskeyError(null); }}>Reset Passkey</Button>
                            <Button variant="danger" onClick={() => { setRemovingDriver(d); setDriverRemovalError(null); }}>Remove Driver</Button>
                          </>}
                          {!d.isActive && <Button variant="ghost" onClick={() => handleRestoreDriver(d)}>Restore</Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {removingDriver && (
            <div className="modal-overlay" onClick={() => !driverRemovalSubmitting && setRemovingDriver(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Remove Driver</h3>
                <p>{removingDriver.name} can no longer log in and disappears from assignment lists. Job history and audit trail are preserved.</p>
                {driverRemovalError && <div className="banner banner-danger">{driverRemovalError}</div>}
                <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                  <Button variant="danger" onClick={handleRemoveDriver} disabled={driverRemovalSubmitting}>
                    {driverRemovalSubmitting ? "Removing…" : "Remove Driver"}
                  </Button>
                  <Button variant="ghost" onClick={() => setRemovingDriver(null)} disabled={driverRemovalSubmitting}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {/* SPEC-67 §3.6: Reset Device confirm — mirrors the Remove-Driver modal. */}
          {resettingDriver && (
            <div className="modal-overlay" onClick={() => !resetDeviceSubmitting && setResettingDriver(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Reset Device</h3>
                <p>{resettingDriver.name} must log in again with their PIN to re-link. Use when they have a new phone or are locked out.</p>
                {resetDeviceError && <div className="banner banner-danger">{resetDeviceError}</div>}
                <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                  <Button variant="danger" onClick={() => void handleResetDevice()} disabled={resetDeviceSubmitting}>
                    {resetDeviceSubmitting ? "Resetting…" : "Reset Device"}
                  </Button>
                  <Button variant="ghost" onClick={() => setResettingDriver(null)} disabled={resetDeviceSubmitting}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {/* SPEC-68 §3.6: Reset Passkey confirm — mirrors the Reset Device modal. */}
          {resettingPasskeyDriver && (
            <div className="modal-overlay" onClick={() => !resetPasskeySubmitting && setResettingPasskeyDriver(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Reset Passkey</h3>
                <p>{resettingPasskeyDriver.name} must enroll again at their next NFC punch-in. Use when they have lost the phone the passkey lived on.</p>
                {resetPasskeyError && <div className="banner banner-danger">{resetPasskeyError}</div>}
                <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                  <Button variant="danger" onClick={() => void handleResetPasskey()} disabled={resetPasskeySubmitting}>
                    {resetPasskeySubmitting ? "Resetting…" : "Reset Passkey"}
                  </Button>
                  <Button variant="ghost" onClick={() => setResettingPasskeyDriver(null)} disabled={resetPasskeySubmitting}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Driver Modal/Inline */}
          {editingDriverId && (
            <div className="modal-overlay" onClick={() => setEditingDriverId(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Edit Driver Details</h3>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    value={editDriverName}
                    onChange={(e) => setEditDriverName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    value={editDriverPhone}
                    onChange={(e) => setEditDriverPhone(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Reset 4-Digit PIN (leave blank to keep current)</label>
                  <input
                    maxLength={4}
                    value={editDriverPin}
                    onChange={(e) => setEditDriverPin(e.target.value)}
                    placeholder="New 4-digit PIN"
                  />
                </div>
                <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                  <Button onClick={() => handleUpdateDriver(editingDriverId)}>
                    Save Updates
                  </Button>
                  <Button variant="ghost" onClick={() => setEditingDriverId(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* TAB 2: Dispatchers (Unit #19 / #20) */}
      {activeTab === "dispatchers" && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2>Dispatchers</h2>
            <Button onClick={() => setShowAddDisp(true)}>+ Add Dispatcher</Button>
          </div>

          {showAddDisp && (
            <form onSubmit={handleCreateDispatcher} className="admin-inline-form">
              <h3>New Dispatcher Account</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="dpUser">Username*</label>
                  <input
                    id="dpUser"
                    value={dispUsername}
                    onChange={(e) => setDispUsername(e.target.value)}
                    placeholder="e.g. dispatch_sarah"
                    required
                  />
                  <FieldError message={dispErrors.username} />
                </div>
                <div className="form-group">
                  <label htmlFor="dpPass">Password (min 6 chars)*</label>
                  <input
                    id="dpPass"
                    type="password"
                    value={dispPassword}
                    onChange={(e) => setDispPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <FieldError message={dispErrors.password} />
                </div>
                <div className="form-group">
                  <label htmlFor="dpName">Full Name*</label>
                  <input
                    id="dpName"
                    value={dispName}
                    onChange={(e) => setDispName(e.target.value)}
                    placeholder="e.g. Sarah Jenkins"
                    required
                  />
                  <FieldError message={dispErrors.name} />
                </div>
                <div className="form-group">
                  <label htmlFor="dpPhone">Phone</label>
                  <input
                    id="dpPhone"
                    value={dispPhone}
                    onChange={(e) => setDispPhone(e.target.value)}
                    placeholder="e.g. 831-555-0199"
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button type="submit" disabled={dispSubmitting}>
                  {dispSubmitting ? "Creating…" : "Save Dispatcher"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAddDisp(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {dispatchersLoading ? (
            <p className="muted">Loading dispatchers…</p>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Device</th>
                    <th>Password</th>
                    <th>Jobs</th>
                    <th>Customers</th>
                    <th>Properties</th>
                    <th>Landfills</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatchers.map((u) => (
                    <Fragment key={u.id}>
                    <tr style={!u.isActive ? { opacity: 0.55 } : undefined}>
                      <td>
                        <code>{u.username}</code>
                      </td>
                      <td>
                        <strong>{u.name}</strong>
                      </td>
                      <td>{u.phone || "—"}</td>
                      {/* SPEC-75 §2.6 — display-only binding chip, the driver
                          card's markup reused. Optional wire field → absent
                          reads as not bound (S5: treat absent as false). */}
                      <td>
                        {u.deviceBound ? (
                          <span className="tag tag-green" title={formatLocalDate(u.deviceBoundAt ?? null)}>
                            Bound{u.deviceBoundAt ? ` ${formatLocalDate(u.deviceBoundAt)}` : ""}
                          </span>
                        ) : (
                          <span className="tag">Not bound</span>
                        )}
                      </td>
                      {/* SPEC-75 §2.6 — lockout chip. Driven ONLY by the wire
                          field; enforcement re-reads the column server-side. */}
                      <td>
                        {u.passwordLocked ? (
                          <span className="tag tag-red">Locked</span>
                        ) : (
                          <span className="tag">OK</span>
                        )}
                      </td>
                      <td>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: togglingDispatcherId === u.id ? "wait" : "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={u.canCreateJobs}
                            disabled={togglingDispatcherId === u.id}
                            onChange={(e) =>
                              void handleToggleFlag(
                                u.id,
                                "canCreateJobs",
                                e.target.checked,
                                u.canCreateJobs,
                                "jobs",
                              )
                            }
                          />
                          <span className={u.canCreateJobs ? "tag tag-green" : "tag tag-red"}>
                            {u.canCreateJobs ? "Enabled" : "Disabled"}
                          </span>
                        </label>
                      </td>
                      <td>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: togglingDispatcherId === u.id ? "wait" : "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={u.canCreateCustomers}
                            disabled={togglingDispatcherId === u.id}
                            onChange={(e) =>
                              void handleToggleFlag(
                                u.id,
                                "canCreateCustomers",
                                e.target.checked,
                                u.canCreateCustomers,
                                "customers",
                              )
                            }
                          />
                          <span className={u.canCreateCustomers ? "tag tag-green" : "tag tag-red"}>
                            {u.canCreateCustomers ? "Enabled" : "Disabled"}
                          </span>
                        </label>
                      </td>
                      <td>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: togglingDispatcherId === u.id ? "wait" : "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={u.canCreateProperties}
                            disabled={togglingDispatcherId === u.id}
                            onChange={(e) =>
                              void handleToggleFlag(
                                u.id,
                                "canCreateProperties",
                                e.target.checked,
                                u.canCreateProperties,
                                "properties",
                              )
                            }
                          />
                          <span className={u.canCreateProperties ? "tag tag-green" : "tag tag-red"}>
                            {u.canCreateProperties ? "Enabled" : "Disabled"}
                          </span>
                        </label>
                      </td>
                      <td>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: togglingDispatcherId === u.id ? "wait" : "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={u.canCreateLandfills}
                            disabled={togglingDispatcherId === u.id}
                            onChange={(e) =>
                              void handleToggleFlag(
                                u.id,
                                "canCreateLandfills",
                                e.target.checked,
                                u.canCreateLandfills,
                                "landfills",
                              )
                            }
                          />
                          <span className={u.canCreateLandfills ? "tag tag-green" : "tag tag-red"}>
                            {u.canCreateLandfills ? "Enabled" : "Disabled"}
                          </span>
                        </label>
                      </td>
                      <td>
                        {!u.isActive ? (
                          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            <span className="tag tag-red">Removed</span>
                            <Button variant="ghost" onClick={() => void handleRestoreDispatcher(u)}>Restore</Button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            <Button
                              variant="ghost"
                              disabled={editDispSubmitting}
                              onClick={() => openEditDispatcher(u)}
                            >
                              Edit
                            </Button>
                            <Button variant="ghost" onClick={() => { setResettingDeviceDispatcher(u); setResetDeviceDispatcherError(null); }}>Reset Device</Button>
                            <Button variant="ghost" onClick={() => { setResettingPasswordDispatcher(u); setResetPasswordDispatcherError(null); setDispNewPassword(""); setDispNewPasswordConfirm(""); }}>Reset Password</Button>
                            <Button variant="danger" onClick={() => { setRemovingDispatcher(u); setDispRemovalError(null); }}>Remove</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {editingDispatcherId === u.id && (
                      <tr>
                        <td colSpan={10}>
                          <form onSubmit={handleUpdateDispatcher} className="admin-inline-form" style={{ marginTop: "8px" }}>
                            <h3>Edit Dispatcher</h3>
                            <div className="form-grid">
                              <div className="form-group">
                                <label htmlFor={`editDispName-${u.id}`}>Full Name*</label>
                                <input
                                  id={`editDispName-${u.id}`}
                                  value={editDispName}
                                  onChange={(e) => setEditDispName(e.target.value)}
                                  required
                                />
                                <FieldError message={editDispErrors.name} />
                              </div>
                              <div className="form-group">
                                <label htmlFor={`editDispUser-${u.id}`}>Username*</label>
                                <input
                                  id={`editDispUser-${u.id}`}
                                  value={editDispUsername}
                                  onChange={(e) => setEditDispUsername(e.target.value)}
                                  required
                                />
                                <FieldError message={editDispErrors.username} />
                              </div>
                              <div className="form-group">
                                <label htmlFor={`editDispPass-${u.id}`}>New password (leave blank to keep current)</label>
                                <input
                                  id={`editDispPass-${u.id}`}
                                  type="password"
                                  value={editDispPassword}
                                  onChange={(e) => setEditDispPassword(e.target.value)}
                                  placeholder="••••••••"
                                />
                                <FieldError message={editDispErrors.password} />
                              </div>
                            </div>
                            {editDispError && (
                              <div className="form-error" style={{ marginBottom: "12px" }}>
                                {editDispError}
                              </div>
                            )}
                            <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                              <Button type="submit" disabled={editDispSubmitting}>
                                {editDispSubmitting ? "Saving…" : "Save Dispatcher"}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                disabled={editDispSubmitting}
                                onClick={() => { setEditingDispatcherId(null); setEditDispErrors({}); setEditDispError(null); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {removingDispatcher && (
            <div className="modal-overlay" onClick={() => !dispRemovalSubmitting && setRemovingDispatcher(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Remove Dispatcher</h3>
                <p>{removingDispatcher.name} can no longer log in and their sessions are revoked. Existing jobs and records are unaffected.</p>
                {dispRemovalError && <div className="banner banner-danger">{dispRemovalError}</div>}
                <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                  <Button variant="danger" onClick={() => void handleRemoveDispatcher()} disabled={dispRemovalSubmitting}>
                    {dispRemovalSubmitting ? "Removing…" : "Remove Dispatcher"}
                  </Button>
                  <Button variant="ghost" onClick={() => setRemovingDispatcher(null)} disabled={dispRemovalSubmitting}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {/* SPEC-75 §2.6: Reset Device confirm — the driver card's modal
              mirrored; keyed on the dispatcher's USER id. */}
          {resettingDeviceDispatcher && (
            <div className="modal-overlay" onClick={() => !resetDeviceDispatcherSubmitting && setResettingDeviceDispatcher(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Reset Device</h3>
                <p>{resettingDeviceDispatcher.name} must log in again to re-link this computer. Use when they have a new machine or are locked out.</p>
                {resetDeviceDispatcherError && <div className="banner banner-danger">{resetDeviceDispatcherError}</div>}
                <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                  <Button variant="danger" onClick={() => void handleResetDispatcherDevice()} disabled={resetDeviceDispatcherSubmitting}>
                    {resetDeviceDispatcherSubmitting ? "Resetting…" : "Reset Device"}
                  </Button>
                  <Button variant="ghost" onClick={() => setResettingDeviceDispatcher(null)} disabled={resetDeviceDispatcherSubmitting}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {/* SPEC-75 §2.6: Reset Password modal — new password + confirm,
              current locked state, rv-bump disclosure (audit I1). */}
          {resettingPasswordDispatcher && (
            <div className="modal-overlay" onClick={() => !resetPasswordDispatcherSubmitting && setResettingPasswordDispatcher(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Reset Password</h3>
                <p>
                  {resettingPasswordDispatcher.name} is {resettingPasswordDispatcher.passwordLocked ? "locked out after 3 failed attempts" : "not currently locked"}. This sets a new password,
                  unlocks the account, and signs them out — they will need to log in again with the new password.
                </p>
                <div className="form-group">
                  <label htmlFor="dispNewPassword">New password (min 6 chars)*</label>
                  <input
                    id="dispNewPassword"
                    type="password"
                    value={dispNewPassword}
                    onChange={(e) => setDispNewPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dispNewPasswordConfirm">Confirm new password*</label>
                  <input
                    id="dispNewPasswordConfirm"
                    type="password"
                    value={dispNewPasswordConfirm}
                    onChange={(e) => setDispNewPasswordConfirm(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                </div>
                {resetPasswordDispatcherError && <div className="banner banner-danger">{resetPasswordDispatcherError}</div>}
                <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                  <Button variant="danger" onClick={() => void handleResetDispatcherPassword()} disabled={resetPasswordDispatcherSubmitting}>
                    {resetPasswordDispatcherSubmitting ? "Resetting…" : "Reset Password"}
                  </Button>
                  <Button variant="ghost" onClick={() => setResettingPasswordDispatcher(null)} disabled={resetPasswordDispatcherSubmitting}>Cancel</Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* TAB 3: Trucks */}
      {activeTab === "trucks" && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2>Trucks Fleet</h2>
            <Button onClick={() => setShowAddTruck(true)}>+ Add Truck</Button>
          </div>

          {showAddTruck && (
            <form onSubmit={handleCreateTruck} className="admin-inline-form">
              <h3>New Truck</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="trUnit">Unit Number*</label>
                  <input
                    id="trUnit"
                    type="number"
                    value={truckUnit}
                    onChange={(e) => setTruckUnit(e.target.value)}
                    placeholder="e.g. 7"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="trMake">Make</label>
                  <input
                    id="trMake"
                    value={truckMake}
                    onChange={(e) => setTruckMake(e.target.value)}
                    placeholder="e.g. Peterbilt"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="trModel">Model</label>
                  <input
                    id="trModel"
                    value={truckModel}
                    onChange={(e) => setTruckModel(e.target.value)}
                    placeholder="e.g. 567 Roll-off"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="trYear">Year</label>
                  <input
                    id="trYear"
                    type="number"
                    value={truckYear}
                    onChange={(e) => setTruckYear(e.target.value)}
                    placeholder="e.g. 2024"
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button type="submit" disabled={truckSubmitting}>
                  {truckSubmitting ? "Adding…" : "Save Truck"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAddTruck(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {editingTruckId && (
            <form onSubmit={updateTruck} className="admin-inline-form">
              <h3>Edit Truck</h3>
              <div className="form-grid">
                <input type="number" value={truckEdit.unitNumber} onChange={(e) => setTruckEdit({ ...truckEdit, unitNumber: e.target.value })} required />
                <input value={truckEdit.make} onChange={(e) => setTruckEdit({ ...truckEdit, make: e.target.value })} placeholder="Make" />
                <input value={truckEdit.model} onChange={(e) => setTruckEdit({ ...truckEdit, model: e.target.value })} placeholder="Model" />
                <input type="number" value={truckEdit.year} onChange={(e) => setTruckEdit({ ...truckEdit, year: e.target.value })} placeholder="Year" />
              </div>
              <Button type="submit" disabled={lifecycleSubmitting === editingTruckId}>{lifecycleSubmitting === editingTruckId ? "Saving…" : "Save Truck"}</Button>{" "}
              <Button type="button" variant="ghost" onClick={() => setEditingTruckId(null)}>Cancel</Button>
            </form>
          )}
          {trucksLoading ? (
            <p className="muted">Loading trucks…</p>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Unit #</th>
                    <th>Make / Model</th>
                    <th>Year</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trucks.map((t) => (
                    <tr key={t.id} style={{ opacity: t.isActive ? 1 : 0.55 }}>
                      <td>
                        <strong>Truck #{t.unitNumber}</strong>
                      </td>
                      <td>{[t.make, t.model].filter(Boolean).join(" ") || "—"}</td>
                      <td>{t.year || "—"}</td>
                      <td>
                        <span className={`tag ${t.isActive ? "tag-green" : "tag-red"}`}>
                          {t.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <Button variant="ghost" onClick={() => openEditTruck(t)}>Edit</Button>{" "}
                        <Button variant={t.isActive ? "danger" : "ghost"} disabled={lifecycleSubmitting === t.id} onClick={() => void toggleTruck(t)}>{t.isActive ? "Deactivate" : "Reactivate"}</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* TAB 4: Dumpster Sizes */}
      {activeTab === "sizes" && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2>Dumpster Sizes</h2>
            <Button onClick={() => setShowAddSize(true)}>+ Add Size</Button>
          </div>

          {showAddSize && (
            <form onSubmit={handleCreateSize} className="admin-inline-form">
              <h3>New Dumpster Size</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="szYards">Yards*</label>
                  <input
                    id="szYards"
                    type="number"
                    value={sizeYards}
                    onChange={(e) => setSizeYards(e.target.value)}
                    placeholder="e.g. 50"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="szLabel">Display Label*</label>
                  <input
                    id="szLabel"
                    value={sizeLabel}
                    onChange={(e) => setSizeLabel(e.target.value)}
                    placeholder="e.g. 50 yd"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="szSort">Sort Order</label>
                  <input
                    id="szSort"
                    type="number"
                    value={sizeSort}
                    onChange={(e) => setSizeSort(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button type="submit" disabled={sizeSubmitting}>
                  {sizeSubmitting ? "Adding…" : "Save Size"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAddSize(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {editingSizeId && (
            <form onSubmit={updateSize} className="admin-inline-form">
              <h3>Edit Dumpster</h3>
              <div className="form-grid">
                <input type="number" value={sizeEdit.yards} onChange={(e) => setSizeEdit({ ...sizeEdit, yards: e.target.value })} required />
                <input value={sizeEdit.label} onChange={(e) => setSizeEdit({ ...sizeEdit, label: e.target.value })} required />
                <input type="number" value={sizeEdit.sortOrder} onChange={(e) => setSizeEdit({ ...sizeEdit, sortOrder: e.target.value })} required />
              </div>
              <Button type="submit" disabled={lifecycleSubmitting === editingSizeId}>{lifecycleSubmitting === editingSizeId ? "Saving…" : "Save Dumpster"}</Button>{" "}
              <Button type="button" variant="ghost" onClick={() => setEditingSizeId(null)}>Cancel</Button>
            </form>
          )}
          {sizesLoading ? (
            <p className="muted">Loading dumpster sizes…</p>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Yards</th>
                    <th>Sort Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sizes.map((s) => (
                    <tr key={s.id} style={{ opacity: s.isActive ? 1 : 0.55 }}>
                      <td>
                        <strong>{s.label}</strong>
                      </td>
                      <td>{s.yards} yards</td>
                      <td>{s.sortOrder}</td>
                      <td>
                        <Button variant="ghost" onClick={() => openEditSize(s)}>Edit</Button>{" "}
                        <Button variant={s.isActive ? "danger" : "ghost"} disabled={lifecycleSubmitting === s.id} onClick={() => void toggleSize(s)}>{s.isActive ? "Deactivate" : "Reactivate"}</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* TAB 5: Landfills */}
      {activeTab === "landfills" && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2>Landfills &amp; Transfer Stations</h2>
            <Button onClick={() => setShowAddLandfill(true)}>+ Add Landfill</Button>
          </div>

          {showAddLandfill && (
            <form onSubmit={handleCreateLandfill} className="admin-inline-form">
              <h3>New Landfill / Transfer Station</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="lfName">Name*</label>
                  <input
                    id="lfName"
                    value={landfillName}
                    onChange={(e) => setLandfillName(e.target.value)}
                    placeholder="e.g. Monterey Regional Waste"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lfAddr">Address</label>
                  <input
                    id="lfAddr"
                    value={landfillAddress}
                    onChange={(e) => setLandfillAddress(e.target.value)}
                    placeholder="e.g. 14201 Del Monte Blvd, Marina, CA"
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button type="submit" disabled={landfillSubmitting}>
                  {landfillSubmitting ? "Adding…" : "Save Landfill"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAddLandfill(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {landfillsLoading ? (
            <p className="muted">Loading landfills…</p>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Address</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {landfills.map((l) => (
                    <tr key={l.id} style={!l.isActive ? { opacity: 0.55 } : undefined}>
                      <td><strong>{l.name}</strong></td>
                      <td>{l.address || "—"}</td>
                      <td>
                        {!l.isActive && <span className="tag tag-red">Inactive</span>} {editingLandfillId === l.id ? (
                          <form onSubmit={handleUpdateLandfill} className="admin-inline-form">
                            <input value={landfillEdit.name} onChange={(e) => setLandfillEdit({ ...landfillEdit, name: e.target.value })} required />
                            <input value={landfillEdit.address} onChange={(e) => setLandfillEdit({ ...landfillEdit, address: e.target.value })} />
                            <Button type="submit" disabled={landfillLifecycleSubmitting === l.id}>Save</Button>
                            <Button type="button" variant="ghost" onClick={() => setEditingLandfillId(null)}>Cancel</Button>
                          </form>
                        ) : <>
                          {l.isActive && <Button variant="ghost" onClick={() => { setEditingLandfillId(l.id); setLandfillEdit({ name: l.name, address: l.address ?? "" }); }}>Edit</Button>}{" "}
                          <Button variant={l.isActive ? "danger" : "ghost"} disabled={landfillLifecycleSubmitting === l.id} onClick={() => void toggleLandfill(l)}>{l.isActive ? "Deactivate" : "Reactivate"}</Button>
                        </>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* TAB 6: Customers & Properties */}
      {activeTab === "customers" && (
        <div className="customers-split">
          {/* Customers Column */}
          <div className="office-col customers-pane">
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h2>Customers</h2>
                <Button
                  onClick={() => {
                    setEditingCustomerId(null);
                    setEditingPropertyId(null);
                    setShowAddProperty(false);
                    setShowAddCustomer(true);
                  }}
                >
                  + Customer
                </Button>
              </div>

              {showAddCustomer && (
                <form onSubmit={handleCreateCustomer} className="admin-inline-form">
                  <h3>New Customer Account</h3>
                  <div className="form-group">
                    <label>Company Name*</label>
                    <input
                      value={custCompany}
                      onChange={(e) => setCustCompany(e.target.value)}
                      placeholder="e.g. Salinas Valley Produce"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Billing Address Line 1*</label>
                    <input
                      value={custLine1}
                      onChange={(e) => setCustLine1(e.target.value)}
                      placeholder="e.g. 100 Main St"
                      required
                    />
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>City*</label>
                      <input
                        value={custCity}
                        onChange={(e) => setCustCity(e.target.value)}
                        placeholder="Salinas"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Zip*</label>
                      <input
                        value={custZip}
                        onChange={(e) => setCustZip(e.target.value)}
                        placeholder="93901"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Contact Name</label>
                    <input
                      value={custContact}
                      onChange={(e) => setCustContact(e.target.value)}
                      placeholder="e.g. John Doe"
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      value={custPhone}
                      onChange={(e) => setCustPhone(e.target.value)}
                      placeholder="e.g. 831-555-0188"
                    />
                  </div>
                  <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                    <Button type="submit" disabled={custSubmitting}>
                      {custSubmitting ? "Creating…" : "Save Customer"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setEditingCustomerId(null);
                        setEditingPropertyId(null);
                        setShowAddCustomer(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}

              {customersLoading ? (
                <p className="muted">Loading customers…</p>
              ) : (
                <>
                  {/* CUSTOMER-MASTER-DETAIL (PM 2026-09-17): client-side name
                      filter — filters the rendered list only, never refetches. */}
                  <input
                    type="search"
                    className="admin-customer-filter"
                    placeholder="Filter customers by name…"
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value)}
                    aria-label="Filter customers by name"
                  />
                  <div className="customer-list">
                    {filteredCustomers.length === 0 && customers.length > 0 ? (
                      <p className="muted">
                        No customers match "{customerFilter.trim()}"
                      </p>
                    ) : (
                      filteredCustomers.map((c) => (
                        <div
                          key={c.id}
                          className={`customer-item ${selectedCustomerId === c.id ? "selected" : ""}`}
                          onClick={() => setSelectedCustomerId(c.id)}
                        >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            <span>{c.companyName}</span>
                            {!c.isActive && <span className="tag tag-red">Inactive</span>}
                          </div>
                          <div className="muted" style={{ fontSize: "13px" }}>
                            {[c.billingCity, c.billingState].filter(Boolean).join(", ")}
                            {c.contactPhone ? ` · ${c.contactPhone}` : ""}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          style={{ padding: "4px 8px", minHeight: "32px", fontSize: "13px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditCustomer(c);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          style={{ padding: "4px 8px", minHeight: "32px", fontSize: "13px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingCustomerId(c.id);
                            setDeleteCustError(null);
                            setDeleteCustReason("OTHER");
                          }}
                        >
                          Delete
                        </Button>
                      </div>

                      {editingCustomerId === c.id && (
                        <form
                          onSubmit={handleUpdateCustomer}
                          className="admin-inline-form"
                          style={{ marginTop: "12px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <h3>Edit Customer</h3>
                          <div className="form-group">
                            <label>Company Name*</label>
                            <input
                              value={editCustCompanyName}
                              onChange={(e) => setEditCustCompanyName(e.target.value)}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Contact Name</label>
                            <input
                              value={editCustContactName}
                              onChange={(e) => setEditCustContactName(e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label>Contact Phone</label>
                            <input
                              value={editCustContactPhone}
                              onChange={(e) => setEditCustContactPhone(e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label>Billing Address Line 1*</label>
                            <input
                              value={editCustLine1}
                              onChange={(e) => setEditCustLine1(e.target.value)}
                              required
                            />
                          </div>
                          <div className="form-grid">
                            <div className="form-group">
                              <label>City*</label>
                              <input
                                value={editCustCity}
                                onChange={(e) => setEditCustCity(e.target.value)}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>State*</label>
                              <input
                                value={editCustState}
                                onChange={(e) => setEditCustState(e.target.value)}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Zip*</label>
                              <input
                                value={editCustZip}
                                onChange={(e) => setEditCustZip(e.target.value)}
                                required
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={editCustActive}
                                onChange={(e) => setEditCustActive(e.target.checked)}
                                style={{ width: "auto", margin: 0 }}
                              />
                              Active Customer
                            </label>
                          </div>
                          <div className="form-group">
                            <label>Reason for edit*</label>
                            <select
                              value={editCustReason}
                              onChange={(e) => setEditCustReason(e.target.value as CustomerEditReason)}
                              required
                            >
                              <option value="WRONG_CUSTOMER">{EDIT_REASON_LABELS.WRONG_CUSTOMER}</option>
                              <option value="OTHER">{EDIT_REASON_LABELS.OTHER}</option>
                            </select>
                          </div>
                          {editCustomerError && (
                            <div className="form-error" style={{ marginBottom: "12px" }}>
                              {editCustomerError}
                            </div>
                          )}
                          <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                            <Button type="submit" disabled={editCustomerSubmitting}>
                              {editCustomerSubmitting ? "Saving…" : "Save Customer"}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => {
                                setEditingCustomerId(null);
                                setEditCustomerError(null);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </form>
                      )}

                      {/* CUSTOMER-DELETE (PM 2026-09-17): inline confirm — the
                          same expanding-form pattern as the Edit Customer form
                          above. Names the customer, warns properties go too,
                          requires a SPEC-44 reason before the DELETE fires. */}
                      {deletingCustomerId === c.id && (
                        <div
                          className="admin-inline-form"
                          style={{ marginTop: "12px", border: "1px solid rgba(190,60,50,0.4)", borderRadius: "6px", padding: "12px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <h3>Delete Customer</h3>
                          <p style={{ margin: "0 0 8px 0" }}>
                            Delete <strong>{c.companyName}</strong>? This removes the
                            customer and <strong>all of its sites</strong>. This
                            cannot be undone.
                          </p>
                          <div className="form-group">
                            <label>Reason for delete*</label>
                            <select
                              value={deleteCustReason}
                              onChange={(e) => setDeleteCustReason(e.target.value as CustomerEditReason)}
                              required
                            >
                              <option value="WRONG_CUSTOMER">{EDIT_REASON_LABELS.WRONG_CUSTOMER}</option>
                              <option value="OTHER">{EDIT_REASON_LABELS.OTHER}</option>
                            </select>
                          </div>
                          {deleteCustError && (
                            <div className="form-error" style={{ marginBottom: "12px" }}>
                              {deleteCustError}
                            </div>
                          )}
                          <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                            <Button
                              type="button"
                              disabled={deleteCustSubmitting}
                              onClick={() => void handleDeleteCustomer()}
                            >
                              {deleteCustSubmitting ? "Deleting…" : "Confirm Delete"}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              disabled={deleteCustSubmitting}
                              onClick={() => {
                                setDeletingCustomerId(null);
                                setDeleteCustError(null);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </Card>
          </div>

          {/* Properties Column */}
          <div className="office-col properties-pane">
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h2>Site Properties</h2>
                {selectedCustomerId && (
                  <Button
                    onClick={() => {
                      setEditingCustomerId(null);
                      setEditingPropertyId(null);
                      setShowAddCustomer(false);
                      setShowAddProperty(true);
                    }}
                  >
                    + Add Site
                  </Button>
                )}
              </div>

              {!selectedCustomerId ? (
                <p className="muted">Select a customer on the left to view and manage their job sites.</p>
              ) : (
                <>
                  {showAddProperty && (
                    <form onSubmit={handleCreateProperty} className="admin-inline-form">
                      <h3>New Job Site Property</h3>
                      <div className="form-group">
                        <label>Site Name*</label>
                        <input
                          value={propSiteName}
                          onChange={(e) => setPropSiteName(e.target.value)}
                          placeholder="e.g. Cooler #3 / Processing Plant"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Site Address Line 1*</label>
                        <input
                          value={propLine1}
                          onChange={(e) => setPropLine1(e.target.value)}
                          placeholder="e.g. 450 Agriculture Way"
                          required
                        />
                      </div>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>City*</label>
                          <input
                            value={propCity}
                            onChange={(e) => setPropCity(e.target.value)}
                            placeholder="Salinas"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Zip*</label>
                          <input
                            value={propZip}
                            onChange={(e) => setPropZip(e.target.value)}
                            placeholder="93901"
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Site Contact Name</label>
                        <input
                          value={propContact}
                          onChange={(e) => setPropContact(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Site Contact Phone</label>
                        <input
                          value={propPhone}
                          onChange={(e) => setPropPhone(e.target.value)}
                        />
                      </div>
                      <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                        <Button type="submit" disabled={propSubmitting}>
                          {propSubmitting ? "Adding…" : "Save Site"}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setEditingCustomerId(null);
                            setEditingPropertyId(null);
                            setShowAddProperty(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  )}

                  {propertiesLoading ? (
                    <p className="muted">Loading properties…</p>
                  ) : properties.length === 0 ? (
                    <div className="banner banner-warning">
                      ⚠️ <strong>No site properties found for this customer.</strong>
                      <p style={{ margin: "4px 0 0 0", fontSize: "13px" }}>
                        Add at least one property to use this customer in jobs.
                      </p>
                    </div>
                  ) : (
                    <div className="property-list">
                      {properties.map((p) => (
                        <div key={p.id} className="property-item">
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                <span>{p.siteName}</span>
                                {!p.isActive && <span className="tag tag-red">Inactive</span>}
                              </div>
                              <div className="muted" style={{ fontSize: "13px" }}>
                                {[p.addressLine1, p.city, p.state, p.zip].filter(Boolean).join(", ")}
                              </div>
                              {p.contactName && (
                                <div className="muted" style={{ fontSize: "12px", marginTop: "4px" }}>
                                  Contact: {p.contactName} {p.contactPhone ? `(${p.contactPhone})` : ""}
                                </div>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              style={{ padding: "4px 8px", minHeight: "32px", fontSize: "13px" }}
                              onClick={() => openEditProperty(p)}
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              style={{ padding: "4px 8px", minHeight: "32px", fontSize: "13px" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setTogglingPropertyId(p.id);
                                setTogglingPropertyTarget(p.isActive ? "inactivate" : "reactivate");
                                setTogglePropError(null);
                                setTogglePropReason("OTHER");
                              }}
                            >
                              {p.isActive ? "Inactivate" : "Reactivate"}
                            </Button>
                          </div>

                          {/* PROPERTY-INACTIVATE (PM 2026-09-17): inline
                              reason-confirm — the same expanding-form pattern
                              as the Edit form below; posts the toggle and
                              refreshes the list. */}
                          {togglingPropertyId === p.id && (
                            <div
                              className="admin-inline-form"
                              style={{ marginTop: "12px", border: "1px solid rgba(190,60,50,0.4)", borderRadius: "6px", padding: "12px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <h3>{togglingPropertyTarget === "inactivate" ? "Inactivate Site" : "Reactivate Site"}</h3>
                              <p style={{ margin: "0 0 8px 0" }}>
                                {togglingPropertyTarget === "inactivate"
                                  ? "Inactivate this site? It will be removed from the job site dropdowns. The customer's other sites are unaffected."
                                  : "Reactivate this site? It will reappear in the job site dropdowns."}
                              </p>
                              <div className="form-group">
                                <label>Reason for {togglingPropertyTarget}*</label>
                                <select
                                  value={togglePropReason}
                                  onChange={(e) => setTogglePropReason(e.target.value as PropertyEditReason)}
                                  required
                                >
                                  <option value="WRONG_PROPERTY">{EDIT_REASON_LABELS.WRONG_PROPERTY}</option>
                                  <option value="OTHER">{EDIT_REASON_LABELS.OTHER}</option>
                                </select>
                              </div>
                              {togglePropError && (
                                <div className="form-error" style={{ marginBottom: "12px" }}>
                                  {togglePropError}
                                </div>
                              )}
                              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                                <Button
                                  type="button"
                                  disabled={togglePropSubmitting}
                                  onClick={() => void handleToggleProperty()}
                                >
                                  {togglePropSubmitting
                                    ? "Working…"
                                    : togglingPropertyTarget === "inactivate"
                                      ? "Confirm Inactivate"
                                      : "Confirm Reactivate"}
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  disabled={togglePropSubmitting}
                                  onClick={() => {
                                    setTogglingPropertyId(null);
                                    setTogglePropError(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                          {editingPropertyId === p.id && (
                            <form
                              onSubmit={handleUpdateProperty}
                              className="admin-inline-form"
                              style={{ marginTop: "12px" }}
                            >
                              <h3>Edit Site Property</h3>
                              <div className="form-group">
                                <label>Site Name*</label>
                                <input
                                  value={editPropSiteName}
                                  onChange={(e) => setEditPropSiteName(e.target.value)}
                                  required
                                />
                              </div>
                              <div className="form-group">
                                <label>Site Address Line 1*</label>
                                <input
                                  value={editPropLine1}
                                  onChange={(e) => setEditPropLine1(e.target.value)}
                                  required
                                />
                              </div>
                              <div className="form-grid">
                                <div className="form-group">
                                  <label>City*</label>
                                  <input
                                    value={editPropCity}
                                    onChange={(e) => setEditPropCity(e.target.value)}
                                    required
                                  />
                                </div>
                                <div className="form-group">
                                  <label>State*</label>
                                  <input
                                    value={editPropState}
                                    onChange={(e) => setEditPropState(e.target.value)}
                                    required
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Zip*</label>
                                  <input
                                    value={editPropZip}
                                    onChange={(e) => setEditPropZip(e.target.value)}
                                    required
                                  />
                                </div>
                              </div>
                              <div className="form-group">
                                <label>Site Contact Name</label>
                                <input
                                  value={editPropContactName}
                                  onChange={(e) => setEditPropContactName(e.target.value)}
                                />
                              </div>
                              <div className="form-group">
                                <label>Site Contact Phone</label>
                                <input
                                  value={editPropContactPhone}
                                  onChange={(e) => setEditPropContactPhone(e.target.value)}
                                />
                              </div>
                              <div className="form-group">
                                <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                                  <input
                                    type="checkbox"
                                    checked={editPropActive}
                                    onChange={(e) => setEditPropActive(e.target.checked)}
                                    style={{ width: "auto", margin: 0 }}
                                  />
                                  Active Property
                                </label>
                              </div>
                              <div className="form-group">
                                <label>Reason for edit*</label>
                                <select
                                  value={editPropReason}
                                  onChange={(e) => setEditPropReason(e.target.value as PropertyEditReason)}
                                  required
                                >
                                  <option value="WRONG_PROPERTY">{EDIT_REASON_LABELS.WRONG_PROPERTY}</option>
                                  <option value="OTHER">{EDIT_REASON_LABELS.OTHER}</option>
                                </select>
                              </div>
                              {editPropertyError && (
                                <div className="form-error" style={{ marginBottom: "12px" }}>
                                  {editPropertyError}
                                </div>
                              )}
                              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                                <Button type="submit" disabled={editPropSubmitting}>
                                  {editPropSubmitting ? "Saving…" : "Save Site"}
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingPropertyId(null);
                                    setEditPropertyError(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </form>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* TAB 7: Tenant Settings (Unit #18) */}
      {activeTab === "settings" && (
        <div className="office-grid">
          {/* Settings form */}
          <div className="office-col">
            <Card>
              <h2>Tenant Configuration</h2>
              <p className="muted" style={{ marginBottom: "16px" }}>
                Operational parameters applied to this tenant. Changes take
                effect immediately for new activity (E/R cooldown gates future
                finish attempts; D/O default billable stamps only on FUTURE
                reviews; phone shows up on the driver "Call dispatch" surface
                on next paint).
              </p>

              {tenantLoading ? (
                <p className="muted">Loading…</p>
              ) : !tenantCfg ? (
                <p className="err">Failed to load tenant config.</p>
              ) : (
                <form onSubmit={handleSaveTenant} className="admin-inline-form">
                  <div className="form-group">
                    <label htmlFor="tsPhone">Office Phone</label>
                    <input
                      id="tsPhone"
                      value={phoneDraft}
                      onChange={(e) => setPhoneDraft(e.target.value)}
                      placeholder="e.g. 831-555-0142"
                    />
                    <FieldError message={tenantFieldErrors.phone} />
                    <p className="muted" style={{ fontSize: "12px", margin: "4px 0 0 0" }}>
                      Shown on the driver "Call dispatch" and "Forgot PIN? Call office" surfaces.
                    </p>
                  </div>

                  {/* DISPATCH-PHONE (PM 2026-09-17): fallback #2 for the
                      driver "Call Dispatch" button — used only when no
                      active-session dispatcher has a phone. */}
                  <div className="form-group">
                    <label htmlFor="tsDispatchPhone">Dispatcher Default Phone</label>
                    <input
                      id="tsDispatchPhone"
                      value={dispatchPhoneDraft}
                      onChange={(e) => setDispatchPhoneDraft(e.target.value)}
                      placeholder="e.g. 831-555-0143"
                    />
                    <FieldError message={tenantFieldErrors.dispatcherDefaultPhone} />
                    <p className="muted" style={{ fontSize: "12px", margin: "4px 0 0 0" }}>
                      Dialed by the driver "Call Dispatch" button when no active dispatcher phone
                      exists (before the Office Phone fallback).
                    </p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="tsEr">E/R Cooldown (minutes)</label>
                    <input
                      id="tsEr"
                      type="number"
                      min={1}
                      max={1440}
                      value={erReturnDraft}
                      onChange={(e) => setErReturnDraft(e.target.value)}
                    />
                    <FieldError message={tenantFieldErrors.erReturnMinutes} />
                    <p className="muted" style={{ fontSize: "12px", margin: "4px 0 0 0" }}>
                      Minimum minutes a driver must wait between an empty/return pickup and tapping FINISH.
                    </p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="tsDo">D/O Default Billable ($)</label>
                    <input
                      id="tsDo"
                      value={doBillableDollarsDraft}
                      onChange={(e) => setDoBillableDollarsDraft(e.target.value)}
                      placeholder="0.00"
                    />
                    <FieldError message={tenantFieldErrors.defaultDoBillableCents} />
                    <p className="muted" style={{ fontSize: "12px", margin: "4px 0 0 0" }}>
                      <strong>Applies to future delivery-only reviews only</strong> —
                      already-reviewed jobs keep their frozen billable amount
                      (16.5 money-snapshot invariant).
                    </p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="tsRet">Photo Retention (months)</label>
                    <input
                      id="tsRet"
                      type="number"
                      min={0}
                      max={600}
                      value={retentionClear ? "" : retentionDraft}
                      onChange={(e) => {
                        setRetentionDraft(e.target.value);
                        setRetentionClear(false);
                      }}
                      disabled={retentionClear}
                    />
                    <div style={{ marginTop: "6px" }}>
                      <label style={{ fontSize: "13px" }}>
                        <input
                          type="checkbox"
                          checked={retentionClear}
                          onChange={(e) => setRetentionClear(e.target.checked)}
                          style={{ marginRight: "6px", width: "auto" }}
                        />
                        No limit (disable purge)
                      </label>
                    </div>
                    <FieldError message={tenantFieldErrors.photoRetentionMonths} />
                    <p className="muted" style={{ fontSize: "12px", margin: "4px 0 0 0" }}>
                      0 = purge all immediately on next press. Max 600 (50 years).
                    </p>
                  </div>

                  <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                    <Button type="submit" disabled={tenantSaving}>
                      {tenantSaving ? "Saving…" : "Save Settings"}
                    </Button>
                  </div>
                </form>
              )}
            </Card>

            {/* Security Keys Card */}
            <div style={{ marginTop: "16px" }}>
              <Card>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                  <div>
                    <h2>Security Keys (YubiKey 2FA)</h2>
                    <p className="muted" style={{ margin: "4px 0 0 0", fontSize: "13px" }}>
                      Registered hardware keys used for two-factor login authentication.
                    </p>
                  </div>
                  <Button
                    onClick={handleStartRegisterKey}
                    disabled={registeringKey || !browserSupportsWebAuthn()}
                  >
                    {registeringKey ? "Registering…" : "+ Register Key"}
                  </Button>
                </div>

                {!browserSupportsWebAuthn() && (
                  <div className="banner banner-danger" style={{ marginBottom: "16px" }}>
                    ⚠️ <strong>Unsupported browser</strong> — This browser does not support security keys.
                  </div>
                )}

                {(tenantCfg?.officeKeyRequired ?? true) && credentials.length === 0 && (
                  <div className="banner banner-warning" style={{ marginBottom: "16px" }}>
                    ⚠️ <strong>No keys registered</strong> — key requirement is not enforced until a key is enrolled.
                  </div>
                )}

                {credentialsLoading ? (
                  <p className="muted">Loading security keys…</p>
                ) : credentials.length === 0 ? (
                  <p className="muted">No security keys enrolled yet.</p>
                ) : (
                  <div className="admin-table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Label</th>
                          <th>Registered</th>
                          <th>Last Used</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {credentials.map((k) => (
                          <tr key={k.id}>
                            <td>
                              <strong>{k.label || "Security Key"}</strong>
                              <div className="muted" style={{ fontSize: "11px" }}>
                                ID: {k.id.slice(0, 16)}…
                              </div>
                            </td>
                            <td>{formatLocalDate(k.registeredAt)}</td>
                            <td>{formatLocalDate(k.lastUsedAt)}</td>
                            <td>
                              <Button
                                variant="ghost"
                                disabled={deletingKeyId === k.id}
                                onClick={() => void handleDeleteKey(k)}
                              >
                                {deletingKeyId === k.id ? "Removing…" : "Remove"}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Security Keys Toggle Footer */}
                <div style={{ marginTop: "16px", padding: "12px", borderRadius: "6px", background: (tenantCfg?.officeKeyRequired ?? true) ? "rgba(40, 140, 80, 0.12)" : "rgba(190, 60, 50, 0.12)", border: `1px solid ${(tenantCfg?.officeKeyRequired ?? true) ? "rgba(40, 140, 80, 0.4)" : "rgba(190, 60, 50, 0.4)"}` }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: 600,
                      cursor: togglingOfficeKey ? "wait" : "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={tenantCfg?.officeKeyRequired ?? true}
                      disabled={togglingOfficeKey}
                      onChange={(e) =>
                        void handleToggleOfficeKeyRequired(
                          e.target.checked,
                          tenantCfg?.officeKeyRequired ?? true,
                        )
                      }
                      style={{ width: "auto", margin: 0 }}
                    />
                    <span>Require Security Key (YubiKey) for office login</span>
                    <span className={(tenantCfg?.officeKeyRequired ?? true) ? "tag tag-green" : "tag tag-yellow"}>
                      {(tenantCfg?.officeKeyRequired ?? true) ? "Enforced" : "Disabled"}
                    </span>
                  </label>
                  <p className="muted" style={{ fontSize: "12px", margin: "6px 0 0 0" }}>
                    Enforces hardware 2FA (WebAuthn/FIDO2) during office password login.
                  </p>
                </div>
              </Card>
            </div>

            {/* SPEC-67 §3.7: Device Binding card — break-glass #2, time-boxed
                tenant-wide toggle. State reads `deviceBindingEnforced` /
                `deviceBindingDisabledUntil` straight off the wire; the
                predicate is derived in SQL from Postgres now() and is NEVER
                recomputed client-side. Optimistic flip + revert mirrors
                handleToggleNfcRequired / handleToggleOfficeKeyRequired. */}
            <div style={{ marginTop: "16px" }}>
              <Card>
                <h2>Device Binding</h2>
                <p className="muted" style={{ margin: "4px 0 12px 0", fontSize: "13px" }}>
                  Refresh tokens only work on the device that logged in. Break-glass:
                  disable tenant-wide for a bounded window — it restores itself, no cron.
                </p>
                <div style={{ marginTop: "8px", padding: "12px", borderRadius: "6px", background: (tenantCfg?.deviceBindingEnforced ?? true) ? "rgba(40, 140, 80, 0.12)" : "rgba(232, 180, 80, 0.12)", border: `1px solid ${(tenantCfg?.deviceBindingEnforced ?? true) ? "rgba(40, 140, 80, 0.4)" : "rgba(232, 180, 80, 0.4)"}` }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: 600,
                      cursor: togglingDeviceBinding ? "wait" : "pointer",
                      flexWrap: "wrap",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={tenantCfg?.deviceBindingEnforced ?? true}
                      disabled={togglingDeviceBinding}
                      onChange={(e) =>
                        void handleToggleDeviceBinding(!e.target.checked, tenantCfg)
                      }
                      style={{ width: "auto", margin: 0 }}
                    />
                    <span>Require device binding on refresh</span>
                    <span className={(tenantCfg?.deviceBindingEnforced ?? true) ? "tag tag-green" : "tag tag-yellow"}>
                      {(tenantCfg?.deviceBindingEnforced ?? true) ? "Enforced" : "Off"}
                    </span>
                    <select
                      aria-label="Disable duration"
                      value={deviceBindingHours}
                      disabled={togglingDeviceBinding || !(tenantCfg?.deviceBindingEnforced ?? true)}
                      onChange={(e) => setDeviceBindingHours(Number(e.target.value))}
                      style={{ width: "auto", margin: 0, marginLeft: "auto" }}
                    >
                      <option value={1}>1 h</option>
                      <option value={4}>4 h</option>
                      <option value={8}>8 h</option>
                      <option value={24}>24 h</option>
                    </select>
                  </label>
                  {!(tenantCfg?.deviceBindingEnforced ?? true) ? (
                    <>
                      <p style={{ fontSize: "12px", margin: "6px 0 0 0", fontWeight: 600, color: "#8A6D00" }}>
                        Disabled until {formatLocalDate(tenantCfg?.deviceBindingDisabledUntil ?? null)} — restores automatically.
                      </p>
                      <p style={{ fontSize: "12px", margin: "6px 0 0 0", color: "#8A6D00" }}>
                        Disabling weakens stolen-cookie protection for every user. Re-enabling may require a one-time re-login on devices that have not connected recently.
                      </p>
                    </>
                  ) : (
                    <p className="muted" style={{ fontSize: "12px", margin: "6px 0 0 0" }}>
                      Refresh tokens are accepted only from the bound device. A driver with a new
                      phone just logs in again; use Reset Device (Drivers tab) for stuck cases.
                    </p>
                  )}
                </div>
              </Card>
            </div>

            {/* Timeclock NFC Tags Registry Card (MOVED FROM TIMECLOCK TAB) */}
            <div style={{ marginTop: "16px" }}>
              <Card>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                  <div>
                    <h2>Timeclock NFC Tags</h2>
                    <p className="muted" style={{ margin: "4px 0 0 0", fontSize: "13px" }}>
                      Registered physical NFC tags used for driver punch verification.
                    </p>
                  </div>
                  <Button variant="primary" onClick={() => setShowRegTag(true)}>
                    + Register Tag
                  </Button>
                </div>

                {nfcTagsLoading ? (
                  <p className="muted">Loading NFC tags…</p>
                ) : nfcTags.length === 0 ? (
                  <p className="muted">No NFC tags registered yet.</p>
                ) : (
                  <div className="admin-table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Label</th>
                          <th>UID</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nfcTags.map((t) => (
                          <tr key={t.id}>
                            <td>{t.label}</td>
                            <td><code>{t.uid}</code></td>
                            <td>
                              {t.isActive ? (
                                <span className="tag tag-green">Active</span>
                              ) : (
                                <span className="tag tag-red">Inactive</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <Button
                                  variant="ghost"
                                  disabled={togglingTagId === t.id || renameSubmitting}
                                  onClick={() => {
                                    setRenamingTag(t);
                                    setRenameLabel(t.label);
                                  }}
                                >
                                  Rename
                                </Button>
                                <Button
                                  variant="ghost"
                                  disabled={togglingTagId === t.id || (renamingTag?.id === t.id && renameSubmitting)}
                                  onClick={() => {
                                    if (t.isActive) {
                                      const ok = window.confirm(
                                        `Deactivate tag "${t.label}"? Drivers will not be able to clock in with it until it is reactivated.`,
                                      );
                                      if (!ok) return;
                                    }
                                    void handleToggleTagActive(t);
                                  }}
                                >
                                  {t.isActive ? "Deactivate" : "Reactivate"}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* NFC Tag Requirement Toggle Footer */}
                <div style={{ marginTop: "16px", padding: "12px", borderRadius: "6px", background: (tenantCfg?.timeclockNfcRequired ?? false) ? "rgba(40, 140, 80, 0.12)" : "rgba(190, 60, 50, 0.12)", border: `1px solid ${(tenantCfg?.timeclockNfcRequired ?? false) ? "rgba(40, 140, 80, 0.4)" : "rgba(190, 60, 50, 0.4)"}` }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: 600,
                      cursor: togglingNfc ? "wait" : "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={tenantCfg?.timeclockNfcRequired ?? false}
                      disabled={togglingNfc}
                      onChange={(e) =>
                        void handleToggleNfcRequired(
                          e.target.checked,
                          tenantCfg?.timeclockNfcRequired ?? false,
                        )
                      }
                      style={{ width: "auto", margin: 0 }}
                    />
                    <span>Require NFC tag for timeclock punches</span>
                    <span className={tenantCfg?.timeclockNfcRequired ? "tag tag-green" : "tag tag-yellow"}>
                      {tenantCfg?.timeclockNfcRequired ? "Enforced" : "Testing Mode"}
                    </span>
                  </label>
                  <p className="muted" style={{ fontSize: "12px", margin: "6px 0 0 0" }}>
                    Off = drivers can punch without scanning — punches recorded as unverified (NO_TAG).
                  </p>
                </div>

                {/* SPEC-68 §4.16: Passkey Enforcement toggle footer — mirrors
                    the NFC footer above (§3.1: the policy OFFERS the passkey
                    first and marks skipped logins as fallback 12 h sessions;
                    it NEVER blocks a login — the PIN/password always works).
                    Chip text uses Enforced / Not required; "Testing Mode"
                    stays unique to NFC. The yellow note shows while NFC is
                    OFF: enrollment needs a qualifying NFC punch (§3.2), so
                    the policy is inert for anyone who has not punched in. */}
                <div style={{ marginTop: "16px", padding: "12px", borderRadius: "6px", background: (tenantCfg?.passkeysRequired ?? false) ? "rgba(40, 140, 80, 0.12)" : "rgba(190, 60, 50, 0.12)", border: `1px solid ${(tenantCfg?.passkeysRequired ?? false) ? "rgba(40, 140, 80, 0.4)" : "rgba(190, 60, 50, 0.4)"}` }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: 600,
                      cursor: togglingPasskeysRequired ? "wait" : "pointer",
                      flexWrap: "wrap",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={tenantCfg?.passkeysRequired ?? false}
                      disabled={togglingPasskeysRequired}
                      onChange={(e) =>
                        void handleTogglePasskeysRequired(
                          e.target.checked,
                          tenantCfg?.passkeysRequired ?? false,
                        )
                      }
                      style={{ width: "auto", margin: 0 }}
                    />
                    <span>Require passkey offer at next NFC punch-in</span>
                    <span className={tenantCfg?.passkeysRequired ? "tag tag-green" : "tag tag-yellow"}>
                      {tenantCfg?.passkeysRequired ? "Enforced" : "Not required"}
                    </span>
                  </label>
                  <p className="muted" style={{ fontSize: "12px", margin: "6px 0 0 0" }}>
                    With this on, drivers and dispatchers must enroll at their next NFC punch-in before using a PIN or password — skipping yields a 12 h fallback session instead of the full 7 days. It never blocks a login.
                  </p>
                  {!(tenantCfg?.timeclockNfcRequired ?? false) && (
                    <p style={{ fontSize: "12px", margin: "6px 0 0 0", fontWeight: 600, color: "#8A6D00" }}>
                      Drivers cannot enroll passkeys while NFC tag scanning is off — enable NFC first.
                    </p>
                  )}
                </div>
              </Card>
            </div>
          </div>

          {/* Purge card */}
          <div className="office-col">
            <Card>
              <h2>Photo Purge</h2>
              <p className="muted" style={{ marginBottom: "16px" }}>
                Permanently deletes photos from R2 storage and the
                <code> job_photos</code> table for jobs in terminal states
                (FINISHED / REVIEWED / CANCELLED) older than the retention
                threshold. <strong>Job records and money fields are NOT
                touched</strong> — only the photo reference and the R2 object.
              </p>

              {tenantCfg && (
                <div className="banner banner-info" style={{ marginBottom: "12px" }}>
                  Current retention: <strong>
                    {tenantCfg.photoRetentionMonths === null
                      ? "No limit (purge disabled)"
                      : tenantCfg.photoRetentionMonths === 0
                        ? "0 months (purge all on next press)"
                        : `${tenantCfg.photoRetentionMonths} month${tenantCfg.photoRetentionMonths === 1 ? "" : "s"}`}
                  </strong>
                </div>
              )}

              {purgeLoading ? (
                <p className="muted">Counting purgeable photos…</p>
              ) : purgePreview !== null ? (
                <div className="banner banner-warning" style={{ marginBottom: "12px" }}>
                  <strong>{purgePreview}</strong> photo{purgePreview === 1 ? "" : "s"} would be purged on the next press.
                </div>
              ) : null}

              {purgeResult && (
                <div
                  className={`banner ${purgeResult.failed > 0 ? "banner-warning" : "banner-success"}`}
                  style={{ marginBottom: "12px" }}
                >
                  {purgeResult.orphanCleanup
                    ? `Orphan cleanup: removed ${purgeResult.purged} unreferenced R2 object(s)`
                    : `Last purge: deleted ${purgeResult.purged} photo(s)${purgeResult.failed > 0 ? `, ${purgeResult.failed} R2 delete(s) failed (see console)` : ""}`}
                </div>
              )}

              {/* Primary purge action with two-step confirm. */}
              {purgeConfirmStep === 0 ? (
                <Button
                  variant="secondary"
                  onClick={() => handlePurge(false)}
                  disabled={purgeRunning}
                >
                  Purge Old Photos…
                </Button>
              ) : purgeConfirmStep === 1 ? (
                <div className="banner banner-danger" style={{ marginTop: "8px" }}>
                  <p style={{ margin: "0 0 8px 0" }}>
                    <strong>Are you sure?</strong> This will permanently delete
                    {" "}{purgePreview ?? 0} photo(s) from R2 storage.
                    Job records and money fields are NOT affected.
                  </p>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                      variant="secondary"
                      onClick={() => void handlePurge(false)}
                      disabled={purgeRunning}
                    >
                      {purgeRunning ? "Purging…" : "Yes, purge now"}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setPurgeConfirmStep(0)}
                      disabled={purgeRunning}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : null}

              {/* Orphan cleanup — secondary action, separate two-step confirm. */}
              {orphanConfirmStep === 0 ? (
                <div style={{ marginTop: "12px" }}>
                  <Button
                    variant="ghost"
                    onClick={() => handlePurge(true)}
                    disabled={purgeRunning}
                  >
                    Clean up R2 orphans…
                  </Button>
                  <p className="muted" style={{ fontSize: "12px", margin: "4px 0 0 0" }}>
                    Removes any R2 objects that have no matching <code>job_photos</code> row
                    (legacy objects from earlier testing). No DB row changes.
                  </p>
                </div>
              ) : (
                <div className="banner banner-warning" style={{ marginTop: "8px" }}>
                  <p style={{ margin: "0 0 8px 0" }}>
                    Delete all R2 objects that aren't referenced by a <code>job_photos</code> row?
                  </p>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                      variant="secondary"
                      onClick={() => void handlePurge(true)}
                      disabled={purgeRunning}
                    >
                      {purgeRunning ? "Cleaning…" : "Yes, clean up"}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setOrphanConfirmStep(0)}
                      disabled={purgeRunning}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
      {/* Rename tag modal */}
      {renamingTag && (
        <div className="modal-overlay" onClick={() => setRenamingTag(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Rename Tag — {renamingTag.uid}</h3>
            <form onSubmit={handleRenameTag} className="form-stack">
              <div className="form-group">
                <label htmlFor="renameTagLabel">New Label</label>
                <input
                  id="renameTagLabel"
                  value={renameLabel}
                  onChange={(e) => setRenameLabel(e.target.value)}
                  placeholder="Breakroom wall"
                />
                <FieldError message={renameErrors.label} />
              </div>
              {renameError && (
                <div className="banner banner-danger">{renameError}</div>
              )}
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <Button type="submit" disabled={renameSubmitting}>
                  {renameSubmitting ? "Saving…" : "Rename"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setRenamingTag(null)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Register NFC tag modal */}
      {showRegTag && (
        <div className="modal-overlay" onClick={() => setShowRegTag(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Register NFC Tag</h3>
            <form onSubmit={handleRegisterTag} className="form-stack">
              <div className="form-group">
                <label htmlFor="regTagUid">Tag UID</label>
                <input
                  id="regTagUid"
                  value={regUid}
                  onChange={(e) => setRegUid(e.target.value)}
                  placeholder="04:B0:11:01:97:57:13"
                />
                <FieldError message={regErrors.uid} />
                <p className="muted" style={{ fontSize: "12px", marginTop: "4px" }}>
                  Colons/hyphens OK — saved uppercase without separators.
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="regTagLabel">Label</label>
                <input
                  id="regTagLabel"
                  value={regLabel}
                  onChange={(e) => setRegLabel(e.target.value)}
                  placeholder="Breakroom wall"
                />
                <FieldError message={regErrors.label} />
              </div>
              {regError && (
                <div className="banner banner-danger">{regError}</div>
              )}
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <Button type="submit" disabled={regSubmitting}>
                  {regSubmitting ? "Registering…" : "Register"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowRegTag(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Security Key Label Prompt Modal */}
      {showKeyLabelModal && (
        <div className="modal-overlay" onClick={() => setShowKeyLabelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Register Security Key</h3>
            <p className="muted" style={{ fontSize: "13px", margin: "0 0 12px 0" }}>
              Give this YubiKey a friendly name to identify it later.
            </p>
            <form onSubmit={handleExecuteRegisterKey} className="form-stack">
              <div className="form-group">
                <label htmlFor="keyLabelInput">Key Label</label>
                <input
                  id="keyLabelInput"
                  value={keyLabelDraft}
                  onChange={(e) => setKeyLabelDraft(e.target.value)}
                  placeholder="e.g. Primary YubiKey 5C"
                  autoFocus
                />
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <Button type="submit">
                  Continue to Touch Key →
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowKeyLabelModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
